/**
 * EN Translation File - Organized by Feature Domain
 *
 * This file contains all English translations for the MyQCare application,
 * organized in a logical hierarchy for easy navigation and maintenance.
 *
 * TABLE OF CONTENTS:
 * ==================
 *
 * 1. CORE/FOUNDATION
 *    └── common - Basic UI actions, states, and reusable text
 *
 * 2. SYSTEM/INFRASTRUCTURE
 *    ├── ui - UI components & accessibility labels
 *    ├── form - Form components & validation messages
 *    ├── error - Error handling messages
 *    ├── theme - Theme & styling related text
 *    ├── navigation - Navigation labels
 *    ├── menu - Menu items & accessibility labels
 *    ├── splash - Splash screen content
 *    ├── logs - Application logging and debugging
 *    └── forceUpgrade - Force app update messages
 *
 * 3. AUTHENTICATION & USER MANAGEMENT
 *    ├── auth - Authentication flows
 *    ├── login - Login process
 *    ├── verify - Verification processes
 *    └── account - Account management
 *
 * 4. USER PROFILE & DATA
 *    ├── profile - User profile management
 *    ├── photo_id - ID verification
 *    ├── address - Address management
 *    └── addressVerification - Address verification
 *
 * 5. HEALTH & MEDICAL
 *    ├── health - General health content
 *    ├── healthHistory - Health history forms
 *    ├── healthAssistance - Health assistance programs
 *    ├── encounter - Encounter information
 *    ├── prescription/prescriptions - Prescription management
 *    ├── labs/lab/hivLab - Lab results and testing
 *    └── assessment/assessments - Health assessments
 *
 * 6. CARE MANAGEMENT
 *    ├── enrollment - Enrollment process
 *    ├── appointment/appointments - Appointment scheduling
 *    ├── home - Home dashboard
 *    ├── chat/chats/asyncMessaging - Messaging systems
 *    └── notifications - Notification messages
 *
 * 7. VISIT
 *    └── visit - Video visit/Twilio components
 *
 * 8. DOCUMENTS & INSURANCE
 *    ├── documents - Document management
 *    ├── insurance/insurances - Insurance handling
 *    ├── consent - Consent forms
 *    └── roiRequest - Release of information
 *
 * 9. PROGRAMS & APPLICATIONS
 *    ├── gileadApplication - Gilead program application
 *    └── welcome - Welcome & onboarding
 *
 * 10. UTILITIES & COMPONENTS
 *    ├── imageUploader - Image upload component
 *    ├── imageCrop - Image crop component
 *    ├── fileUploader - File upload component
 *    ├── documentUploader - Document upload component
 *    ├── pdfViewer - PDF viewer component
 *    ├── carousel - Carousel component
 *    └── miniGame - Mini game (2048) component
 *
 * 11. FINANCIAL
 *    └── payment - Payment processing
 *
 * NAMING CONVENTIONS:
 * ===================
 * - Use dot notation for nested organization (e.g., 'common.save')
 * - Group related functionality under same namespace
 * - Use descriptive, action-oriented keys
 * - Maintain consistency in naming patterns
 *
 * MAINTENANCE NOTES:
 * ==================
 * - Keep translations alphabetically sorted within each namespace
 * - Add new translations to the appropriate section
 * - Update this index when adding new namespaces
 * - Preserve all translation values when reorganizing
 */

const en = {
  // Core/Foundation
  'common.add': 'Add',
  'common.cancel': 'Cancel',
  'common.clear': 'Clear',
  'common.confirm': 'Confirm',
  'common.continue': 'Continue',
  'common.complete': 'Complete',
  'common.conjunction.and': 'and',
  'common.conjunction.comma': ',',
  'common.copy': 'Copy',
  'common.create': 'Create',
  'common.date.today': 'Today',
  'common.date.tomorrow': 'Tomorrow',
  'common.date.yesterday': 'Yesterday',
  'common.delete': 'Delete',
  'common.done': 'Done',
  'common.edit': 'Edit',
  'common.edit.a11y': 'Edit {item}',
  'common.edit.title': 'Edit {title}',
  'common.error': 'Error',
  'common.goBack': 'Go back',
  'common.image': 'Image',
  'common.loading': 'Loading...',
  'common.logout': 'Logout',
  'common.more': 'More',
  'common.more.a11y': 'Load more items',
  'common.new': 'New',
  'common.new.title': 'New {title}',
  'common.no': 'No',
  'common.ok': 'OK',
  'common.options': 'Options',
  'common.options.no': 'No',
  'common.options.notSure': 'Not Sure',
  'common.options.yes': 'Yes',
  'common.progress': 'Progress: {value}%',
  'common.refresh': 'Refresh',
  'common.reload': 'Reload',
  'common.remove': 'Remove',
  'common.reset': 'Reset',
  'common.restart': 'Restart app',
  'common.save': 'Save',
  'common.select': 'Select',
  'common.settings': 'Settings',
  'common.skip': 'Skip',
  'common.success': 'Success',
  'common.tryAgain': 'Try again',
  'common.update': 'Update',
  'common.view': 'View',
  'common.upload': 'Upload',
  'common.yes': 'Yes',
  'common.close': 'Close',
  'common.send': 'Send',
  'common.empty.title': 'No data available',
  'common.empty.description': 'There is no information to display at this time.',

  // Network Status
  'network.offline': 'You are offline',
  'network.back_online': 'Back online',

  // UI Components & Accessibility
  'ui.accordion.a11y.collapseHint': 'Tap to collapse',
  'ui.accordion.a11y.collapsed': 'Collapsed',
  'ui.accordion.a11y.expandHint': 'Tap to expand',
  'ui.accordion.a11y.expanded': 'Expanded',
  'ui.alert.close': 'Close alert',
  'ui.avatar.a11y.default': 'Profile picture',
  'ui.avatar.a11y.label': '{name} profile picture',
  'ui.groupAvatar.a11y.label': 'Group avatar with {count} members',
  'ui.badge.a11y.count': '{count} notifications',
  'ui.badge.a11y.label': '{content} notification',
  'ui.bottomsheet.a11y.dismiss': 'Dismiss',
  'ui.cancelButton.a11y.hint': 'Cancels current action and goes back',
  'ui.chip.a11y.hint.default': 'Tap to select',
  'ui.chip.a11y.label': '{label}',
  'ui.confirmButton.a11y.hint': 'Confirms and saves current action',
  'ui.content.link.aria': 'Link: {text}',
  'ui.content.link.hint': 'Press to open link',
  'ui.content.youtube.aria': 'YouTube Video',
  'ui.content.youtube.hint': 'Press to open video in YouTube',
  'ui.dialog.a11y.actionHint': 'Performs action and closes dialog',
  'ui.dialog.a11y.actionKeepOpenHint': 'Performs action',
  'ui.dialog.a11y.actionsLabel': 'Dialog actions',
  'ui.dialog.a11y.contentLabel': 'Dialog content',
  'ui.dialog.a11y.close': 'Close dialog',
  'ui.dialog.a11y.dismiss': 'Dismiss dialog',
  'ui.dialog.a11y.dismissHint': 'Press to close this dialog',
  'ui.dialog.a11y.headerLabel': 'Dialog header',
  'ui.dialog.a11y.label': 'Dialog',
  'ui.leavePrompt.unsavedChanges.title': 'Unsaved Changes',
  'ui.leavePrompt.unsavedChanges.message': 'You have unsaved changes. Are you sure you want to leave this page?',
  'ui.leavePrompt.unsavedChanges.stay': 'Stay',
  'ui.leavePrompt.unsavedChanges.leave': 'Leave',
  'ui.leavePrompt.booking.title': 'Leave before booking?',
  'ui.leavePrompt.booking.message': "Your appointment isn't scheduled yet. It only takes a minute to finish.",
  'ui.leavePrompt.booking.stay': 'Keep booking',
  'ui.leavePrompt.booking.leave': 'Leave',
  'ui.leavePrompt.asyncVisit.title': 'Leave before submitting your visit?',
  'ui.leavePrompt.asyncVisit.message': "Your progress is saved, but your provider can't review it until you finish.",
  'ui.leavePrompt.asyncVisit.stay': 'Keep going',
  'ui.leavePrompt.asyncVisit.leave': 'Leave',
  'ui.leavePrompt.asyncAssessment.title': 'Leave before finishing the questionnaire?',
  'ui.leavePrompt.asyncAssessment.message': "Your answers are saved, but your visit can't be submitted until you finish.",
  'ui.leavePrompt.asyncAssessment.stay': 'Keep going',
  'ui.leavePrompt.asyncAssessment.leave': 'Leave',
  'ui.leavePrompt.asyncVisitCancel.title': 'Cancel your visit request?',
  'ui.leavePrompt.asyncVisitCancel.message':
    "This can't be undone, and you'd have to start a new request from the beginning. Need a break? Your progress is saved, so you can finish later.",
  'ui.leavePrompt.asyncVisitCancel.stay': 'Keep going',
  'ui.leavePrompt.asyncVisitCancel.leave': 'Yes, cancel',
  'ui.leavePrompt.assessment.title': 'Leave before finishing the questionnaire?',
  'ui.leavePrompt.assessment.message': 'Your answers are saved, and you can come back to finish later.',
  'ui.leavePrompt.assessment.stay': 'Keep going',
  'ui.leavePrompt.assessment.leave': 'Leave',
  'ui.leavePrompt.gileadApplication.title': 'Leave before submitting your application?',
  'ui.leavePrompt.gileadApplication.message': "Your answers are saved on this device, but your application isn't submitted until you sign.",
  'ui.leavePrompt.gileadApplication.stay': 'Keep going',
  'ui.leavePrompt.gileadApplication.leave': 'Leave',
  'ui.leavePrompt.roiRequest.title': 'Leave before signing your request?',
  'ui.leavePrompt.roiRequest.message': "Your answers are saved on this device, but your request isn't complete until you sign.",
  'ui.leavePrompt.roiRequest.stay': 'Keep going',
  'ui.leavePrompt.roiRequest.leave': 'Leave',
  'ui.leavePrompt.visit.title': 'Leave the call?',
  'ui.leavePrompt.visit.message': "A call is in progress. If you leave now, you'll be disconnected.",
  'ui.leavePrompt.visit.stay': 'Stay on the call',
  'ui.leavePrompt.visit.leave': 'Leave',
  'ui.leavePrompt.onDemand.title': 'Leave the waiting room?',
  'ui.leavePrompt.onDemand.message': 'A provider may join at any moment. If you leave, you could miss your visit.',
  'ui.leavePrompt.onDemand.stay': 'Keep waiting',
  'ui.leavePrompt.onDemand.leave': 'Leave',
  'ui.form.progress.a11y.collapseHint': 'Press to collapse completed items',
  'ui.form.progress.a11y.expandHint': 'Press to expand completed items',
  'ui.form.progress.a11y.header': 'Form progress: {completed} of {total} completed',
  'ui.form.progress.completed': '{completed}/{total} Complete',
  'ui.form.progress.item.a11y.completed': '{label} - completed',
  'ui.form.progress.item.a11y.disabled': '{label} - disabled',
  'ui.form.progress.item.a11y.pending': '{label} - pending',
  'ui.list.item.a11y.hint': 'Tap to {action}',
  'ui.loading.subtitle': 'Please wait while we prepare your experience...',
  'ui.loading.title': 'Loading',
  'ui.logo.a11y.label': 'MyQCare logo',
  'ui.progress.a11y.label': 'Progress: {percent} percent complete',
  'ui.progress.stepper.a11y.completed': 'completed',
  'ui.progress.stepper.a11y.current': 'current step',
  'ui.progress.stepper.a11y.pending': 'pending',
  'ui.progress.stepper.a11y.step': 'Step {current} of {total}',
  'ui.refresh.hint': 'Tap to refresh',
  'ui.refresh.label': 'Refresh',
  'ui.signature.a11y.area': '{label} signature area',
  'ui.signature.a11y.hint': 'Draw your signature on this area using your finger or stylus',
  'ui.signature.a11y.signed': '{label} signature completed',
  'ui.signature.clear': 'Clear Signature',
  'ui.signature.clear.hint': 'Remove the current signature to start over',
  'ui.signature.label': 'Signature',
  'ui.slider.a11y.label': '{label} slider',
  'ui.slider.a11y.range': 'Range from {min} to {max}',
  'ui.slider.a11y.value': 'Value: {value}',
  'ui.steps.item.a11y.label': 'Step {number}: {text}',
  'ui.collapse': 'Collapse {label}',
  'ui.expand': 'Expand {label}',
  'ui.tabs.a11y.activeTabHint': 'Currently selected tab',
  'ui.tabs.a11y.inactiveTabHint': 'Tap to switch to this tab',
  'ui.tabs.a11y.label': 'Tab navigation',
  'ui.typography.heading.level': 'Heading level {level}',
  'ui.header.a11y.label': 'Application Header',
  'ui.menu.a11y.label': 'Menu navigation',
  'ui.menu.a11y.open': 'Open menu',

  // Form Components & Validation
  'form.address.loading': 'Loading addresses...',
  'form.address.no.options': 'No addresses found',
  'form.address.placeholder': 'Select an address',
  'form.address.search': 'Search for an address',
  'form.autocomplete.a11y.bottomsheet': 'Select an option',
  'form.autocomplete.a11y.hint': 'Opens a searchable list of options',
  'form.autocomplete.a11y.options.count': '{count} options available',
  'form.autocomplete.a11y.search.hint': 'Type to search for options',
  'form.autocomplete.a11y.selected': 'Selected: {label}',
  'form.autocomplete.min.chars': 'Type at least {count} characters to search',
  'form.autocomplete.placeholder': 'Select an option',
  'form.autocomplete.search.placeholder': 'Search...',
  'form.card.consent.description': 'Type your initials below to consent to medical review or treatment by a licensed provider.',
  'form.card.consent.title': 'Consent to Care',
  'form.card.referral.description': 'Please select the team member who referred you',
  'form.card.referral.title': 'Referral Information',
  'form.checkbox.a11y.checked': '{label}, checked',
  'form.checkbox.a11y.checked.unlabeled': 'Checked',
  'form.checkbox.a11y.unchecked': '{label}, unchecked',
  'form.checkbox.a11y.unchecked.unlabeled': 'Unchecked',
  'form.datepicker.a11y.hint.date': 'Opens date picker',
  'form.datepicker.a11y.hint.datetime': 'Opens date and time picker',
  'form.datepicker.a11y.hint.time': 'Opens time picker',
  'form.datepicker.a11y.selected.date': 'Selected date: {value}',
  'form.datepicker.a11y.selected.datetime': 'Selected date and time: {value}',
  'form.datepicker.a11y.selected.time': 'Selected time: {value}',
  'form.datepicker.placeholder.date': 'Select a date',
  'form.datepicker.placeholder.datetime': 'Select date and time',
  'form.datepicker.placeholder.time': 'Select a time',
  'form.datepicker.title.date': 'Select Date',
  'form.datepicker.title.datetime': 'Select Date and Time',
  'form.datepicker.title.time': 'Select Time',
  'form.datepicker.a11y.hint.input': 'Enter the date as month, day, year',
  'form.datepicker.placeholder.format': 'MM/DD/YYYY',
  'form.datepicker.validation.format': 'Enter a valid date in MM/DD/YYYY format',
  'form.datepicker.validation.minDate': 'Date must be on or after {date}',
  'form.datepicker.validation.maxDate': 'Date must be on or before {date}',
  'form.datepicker.validation.invalid': 'Invalid Date',
  'form.datepicker.validation.todayMax': "Must not exceed today's date",
  'form.datepicker.validation.dateMin': 'Must be after 01/01/1901',
  'form.datepicker.validation.datesOrder': 'End date must be after start date',
  'form.datepicker.validation.datesSame': 'Cannot be the same date',
  'form.date.day': 'Day',
  'form.date.month': 'Month',
  'form.date.year': 'Year',
  'form.dob.ageRestriction': 'Must be at least 18 years old',
  'form.dob.hint': 'Select your date of birth to verify your identity',
  'form.dob.invalid': 'Invalid Date',
  'form.dob.label': 'Date of Birth',
  'form.dob.placeholder': 'Select your date of birth',
  'form.email.hint': 'Enter your email address',
  'form.email.invalid': 'Invalid email format',
  'form.email.label': 'Email',
  'form.email.maxAttempts': 'Too many attempts. Please try again later.',
  'form.email.placeholder': 'Enter your email',
  'form.error.invalid': 'Invalid',
  'form.error.label': 'Error',
  'form.error.max': 'Maximum is {max}',
  'form.error.maxLength': 'Cannot exceed {max} characters',
  'form.error.min': 'Minimum is {min}',
  'form.error.required': 'Required',
  'form.field.required': 'required',
  'form.help.label': 'Help',
  'form.initials.consent': 'We require your consent for the exam and possible treatment',
  'form.initials.label': 'Initials',
  'form.initials.placeholder': 'e.g. JD',
  'form.initials.validation': 'Must be initials of first and last name',
  'form.input.a11y.field': 'Input field {name}',
  'form.password.label': 'Password',
  'form.phone.a11y.hint': 'Enter your 10-digit phone number with area code',
  'form.phone.a11y.label': 'Phone number input field',
  'form.phone.invalid': 'Invalid phone number format',
  'form.phone.label': 'Phone Number',
  'form.phone.placeholder': '(123) 456-7890',
  'form.url.invalid': 'Invalid URL format',
  'form.referral.by.helper': 'Please select the team member who referred you',
  'form.referral.by.label': 'Referred By',
  'form.referral.by.placeholder': 'Select team member who referred you',
  'form.referral.details.label': 'Referral Details',
  'form.referral.details.placeholder': 'Please provide details about who referred you',
  'form.select.a11y.bottomsheet.multiple': 'Select options',
  'form.select.a11y.bottomsheet.single': 'Select an option',
  'form.select.a11y.hint.multiple': 'Opens a list of options for multiple selection',
  'form.select.a11y.hint.single': 'Opens a list of options',
  'form.select.a11y.options.count': '{count} options available',
  'form.select.a11y.search.hint': 'Type to filter the available options',
  'form.select.a11y.selected.multiple': '{count} items selected',
  'form.select.a11y.selected.single': 'Selected: {label}',
  'form.select.a11y.clear': 'Clear selection',
  'form.select.a11y.clear.hint': 'Remove current selection',
  'form.select.all': 'Check all that apply',
  'form.select.empty': 'No options found',
  'form.select.loading': 'Loading options...',
  'form.select.option.a11y.checked': 'checked',
  'form.select.option.a11y.disabled': 'disabled',
  'form.select.option.a11y.not.selected': 'not selected',
  'form.select.option.a11y.selected': 'selected',
  'form.select.placeholder.multiple': 'Select options',
  'form.select.placeholder.single': 'Select an option',
  'form.select.search.placeholder': 'Search options...',

  // Error Handling
  'error.boundary.message': "The application encountered an unexpected error. We've logged the issue and are working to fix it.",
  'error.boundary.title': 'Unexpected Error',
  'error.copied': 'Error details copied to clipboard',
  'error.general.message': 'We apologize for the inconvenience. Please try again or contact support if the problem persists.',
  'error.general.title': 'Something went wrong',
  'error.notAuthorized.message': 'You do not have permission to access this resource.',
  'error.notAuthorized.title': 'Not authorized',
  'error.notFound.message': 'The page you are looking for does not exist or has been moved.',
  'error.notFound.title': 'Page not found',
  'error.conversation.permission.message': "You don't have permission to start a conversation with this user",
  'error.conversation.permission.title': 'Access Denied',
  'error.tapToCopy': 'Tap to copy error details',
  'error.tryAgain': 'Please try again',
  'error.translationUnavailable.title': 'Translation unavailable',
  'error.translationUnavailable.description':
    'We regret to inform you that a technical issue is currently preventing us from fully supporting the translation feature.\n\nTo ensure a seamless experience, we recommend temporarily disabling it.\n\nWe apologize for any inconvenience this may cause and appreciate your understanding as we actively work to resolve this issue.\n\nThank you for your patience.',
  'error.offline.title': 'No Internet Connection',
  'error.offline.message': 'Please check your internet connection and try again. You need to be online to use this app.',
  'error.offline.tips.title': 'Connection Tips:',
  'error.offline.tips.wifi': 'Check your Wi-Fi connection',
  'error.offline.tips.cellular': 'Verify your cellular data is enabled',
  'error.offline.tips.airplane': 'Make sure airplane mode is turned off',
  'error.page.a11y.errorCode': 'Error code {code}',
  'error.page.details.title': 'Error Details',
  'error.page.viewError': 'View Error',
  'error.page.tryAgain': 'Try Again',
  'error.page.messageSupport': 'Message Support',
  'appUpdated.title': 'New updates deployed!',
  'appUpdated.improvements': "We've made some improvements.",
  'appUpdated.reloadPrompt': 'Reload the page to experience the latest features.',
  'appUpdated.toast': 'Updated to the latest version',
  'appUpdated.available': 'A new version is available.',

  // Theme & Styling
  'theme.options.dark': 'Dark',
  'theme.options.light': 'Light',
  'theme.options.system': 'System',

  // Navigation
  'navigation.back': 'Back',
  'navigation.next': 'Next',
  'navigation.chat': 'Messages',
  'navigation.closePage': 'Close page',
  'navigation.to.a11y': 'Navigate to {label}',
  'navigation.developerOptions': 'Developer Options',
  'navigation.health': 'Health',
  'navigation.home': 'Home',
  'navigation.profile': 'Profile',
  'navigation.tabs.a11y.badge': '{label}, {count} pending',
  'navigation.tabs.a11y.label': 'Main navigation',

  // Menu
  'menu.environment': 'Environment (non-prod)',
  'menu.a11y.mainMenu': 'Main menu',
  'menu.a11y.notificationsCount': '{count} new notifications',
  'menu.a11y.fieldErrorsCount': '{count} profile fields have errors',
  'menu.a11y.fieldErrorsComplete': 'All profile fields are complete',
  'menu.a11y.unreadMessages': '{count} unread messages',
  'menu.a11y.noUnreadMessages': 'No unread messages',
  'menu.a11y.patientName': 'Patient name: {name}',
  'menu.a11y.patientEmail': 'Patient email: {email}',
  'menu.a11y.poweredByQCare': 'Powered by QCare',

  // Splash Screen
  'splash.tagline': 'Your health, our priority',

  // Logs
  'logs.screen.title': 'App Logs',
  'logs.filters.title': 'Filter Logs',
  'logs.filters.description': 'Choose which log levels and criteria to display',
  'logs.filters.searchTerm.label': 'Search in Messages',
  'logs.filters.searchTerm.placeholder': 'Search in log messages...',
  'logs.filters.levels.label': 'Log Levels',
  'logs.filters.namespaces.label': 'Namespaces',
  'logs.filters.startDate.label': 'Start Date',
  'logs.filters.endDate.label': 'End Date',
  'logs.filters.clearAll': 'Clear all filters',
  'logs.filters.clearAll.a11y.label': 'Clear all filters',
  'logs.filters.clear.button.a11y.label': 'Clear filters',
  'logs.filters.clear.button.a11y.hint': 'Remove all active filters',
  'logs.filters.filterButton.a11y.label': 'Filter application logs',
  'logs.export.title': 'Export Logs',
  'logs.export.description': 'Choose export format',
  'logs.export.text': 'Export as TXT',
  'logs.export.json': 'Export as JSON',
  'logs.export.success.title': 'Export Complete',
  'logs.export.success.message': 'Logs exported to: {path}',
  'logs.export.error.title': 'Export Failed',
  'logs.export.error.message': 'Failed to export logs.',
  'logs.export.button.a11y.label': 'Export logs',
  'logs.clear.title': 'Clear All Logs',
  'logs.clear.message': 'Are you sure you want to clear all logs? This action cannot be undone.',
  'logs.clear.button.a11y.label': 'Clear all logs',
  'logs.clear.button.a11y.hint': 'Press to clear all logs permanently',
  'logs.entry.copied.title': 'Log copied to clipboard',
  'logs.entry.copied.message': '{level} log from {namespace}',
  'logs.entry.a11y.label': '{level} log entry from {namespace} at {time}',
  'logs.entry.a11y.hint': 'Press to copy log details to clipboard',
  'logs.stats.total': 'Total',
  'logs.stats.errors': 'Errors',
  'logs.stats.warnings': 'Warnings',
  'logs.empty.title': 'No logs found',
  'logs.empty.description': 'Try adjusting your filters or clearing them to see more logs',
  'logs.level.error': 'Error',
  'logs.level.warn': 'Warning',
  'logs.level.info': 'Info',
  'logs.level.debug': 'Debug',
  'logs.filters.searchTerm.a11y.hint': 'Enter text to search within log messages',
  'logs.filters.levels.a11y.hint': 'Select one or more log levels to filter by',
  'logs.filters.namespaces.a11y.hint': 'Select one or more namespaces to filter by',
  'logs.export.button.a11y.hint': 'Press to export logs in different formats',

  // Force App Update
  'forceUpgrade.description':
    'A new version of MyQCare is available with important updates and improvements.\n\nPlease update your app to continue using all features.',
  'forceUpgrade.footer': 'This update is required to continue using MyQCare safely and securely.',
  'forceUpgrade.title': 'Update Required',
  'forceUpgrade.updateButton': 'Update Now',

  // Authentication
  'auth.code.bothSent': "I've sent a verification code to {phone} and {email}.",
  'auth.code.countdownMessage': 'You can request a new code in {seconds} seconds',
  'auth.code.dialog.contentEmail': 'We can resend the verification code to your email.',
  'auth.code.dialog.contentSMS': 'We can resend the verification code via SMS or send it to your email instead.',
  'auth.code.dialog.resendEmail': 'Resend Email',
  'auth.code.dialog.resendSMS': 'Resend SMS',
  'auth.code.dialog.supportNote': 'If you are still having issues, please contact customer support.',
  'auth.code.dialog.title': 'Resend Verification Code',
  'auth.code.dialog.useEmail': 'Use Email Instead',
  'auth.code.emailSent': "I've sent a verification code to {email}.",
  'auth.code.enterBelow': 'Enter the 6-digit code below to continue',
  'auth.code.expired': 'Verification code has expired. Please request a new one.',
  'auth.code.invalid': 'Invalid verification code. Please try again.',
  'auth.code.maxAttempts': 'Too many verification attempts. Please wait before trying again.',
  'auth.code.messageSent': "I've sent a verification code to {phone}.",
  'auth.code.resendCode': "Didn't receive the code? Resend",
  'auth.code.retry.hint': 'Retry sending verification code',
  'auth.code.screenTitle': 'Verification Code',
  'auth.code.backToLogin': 'Back to Login',
  'auth.verify.expired': 'Your login session has expired',
  'auth.sessionExpired.title': 'Session Expired',
  'auth.sessionExpired.description': 'Please login again',
  'auth.verify.greeting.appointment': 'Hi {name},',
  'auth.verify.greeting.appointmentNoName': 'Hi,',
  'auth.verify.greeting.normal': 'Welcome back {name},',
  'auth.verify.greeting.normalNoName': 'Welcome back,',
  'auth.verify.screenTitle': 'Verify Login with Date of Birth',
  'auth.verify.submit': 'Verify & Login',
  'auth.verify.subtitle.appointment': 'Before your appointment starts we need to verify your birthdate',
  'auth.verify.subtitle.normal': "Enter your birthdate below so we can confirm it's you",
  'auth.verify.error.linkNotWorking.title': "That link doesn't seem to be working.",
  'auth.verify.error.linkNotWorking.description': 'Try logging in again.',
  'auth.verify.error.linkNotWorking.button': 'Login again',

  // Link Sent Process
  'auth.linkSent.messageSent.sms': "I've sent a message to {phone}.",
  'auth.linkSent.messageSent.email': "I've sent an email to {maskedEmail}.",
  'auth.linkSent.messageSent.both': "I've sent a message to {phone} and an email to {maskedEmail}.",
  'auth.linkSent.description.single': 'It contains a link to login.',
  'auth.linkSent.description.plural': 'They contain links to login.',
  'auth.linkSent.retry.sms': "I didn't get the message.",
  'auth.linkSent.retry.email': "I didn't get the email.",
  'auth.linkSent.retry.both': "I didn't get the message or the email.",

  // Link Retry Process
  'auth.linkRetry.title.email': 'We can resend the link.',
  'auth.linkRetry.title.sms': 'We can resend the link or you can try with your email address.',
  'auth.linkRetry.supportNote': 'If you are still having issues, please contact customer support.',
  'auth.linkRetry.button.sendMessage': 'Send a new message',
  'auth.linkRetry.button.useEmail': 'Use email instead',

  // Channel Verified Process
  'auth.channelVerified.greeting': 'Great {name},',
  'auth.channelVerified.verified.email': 'Thanks for verifying your email.',
  'auth.channelVerified.verified.phone': 'Thanks for verifying your phone.',
  'auth.channelVerified.button.login': 'Login',
  'auth.channelVerified.error.title': "That link doesn't seem to be working.",
  'auth.channelVerified.error.description': 'Try logging in.',

  // Login Process
  'login.screenTitle': 'Login Screen',
  'login.signIn': 'Sign in',
  'login.signIn.hint': 'Authenticate with your credentials',
  'login.title': 'Sign in',

  // Verification Processes
  'verify.phone.codeMessage': "I've sent a code to {phone}.",
  'verify.phone.countdownMessage': 'You can request a new code in {seconds} seconds',
  'verify.phone.dialog.content': 'We can resend the verification code to your phone.',
  'verify.phone.dialog.resend': 'Resend SMS',
  'verify.phone.dialog.title': 'Resend Verification Code',
  'verify.phone.enterBelow': 'Enter it below so we can verify your phone number.',
  'verify.phone.incorrectCode': "Sorry, that's the wrong code. Please try again.",
  'verify.phone.resend.a11y.hint': 'Press to request a new verification code',
  'verify.phone.resend.a11y.label': 'Resend verification code',
  'verify.phone.resendCode': 'Resend the code',
  'verify.phone.screen.a11y.label': 'Phone verification screen',
  'verify.phone.success': 'Phone verified successfully',
  'verify.phone.title': 'Verify Phone Number',
  'verify.phone.troubleshootingContent':
    "• Check that the phone number you entered is correct.\n• Check that your phone isn't blocking the QCare+ number.\n\nIf you are still having trouble receiving messages please contact customer support.",

  // Account Management
  'account.screen.a11y.accountSection': 'Account management options',
  'account.screen.a11y.label': 'Account settings and profile management',
  'account.screen.a11y.logoutHint': 'Signs you out of the app',
  'account.screen.a11y.settingsSection': 'App settings and preferences',
  'account.screen.a11y.supportSection': 'Help and support options',
  'account.screen.a11y.userInfo': 'User profile information',

  // User Profile Management
  'profile.aboutMe.subtitle': 'Demographics and identity',
  'profile.aboutMe.title': 'About Me',
  'profile.account': 'Account',
  'profile.blank.title': 'Pick a setting to open it here',
  'profile.address.subtitle': 'Your home and shipping address',
  'profile.address.title': 'Address',
  'profile.section.closeLabel': 'Close {label}',
  'profile.section.goBack': 'Press to go back',
  'profile.appVersion': 'App version {version}',
  'profile.badge.a11y.complete': 'Profile complete',
  'profile.badge.a11y.incomplete': '{count} incomplete profile items',
  'profile.complete.title': 'Complete Your Profile',
  'profile.contact.description': 'Please let us know the best way to contact you',
  'profile.contact.description.a11y.label': 'Contact preference information',
  'profile.contact.info':
    'Our team may reach out regarding your care or if additional information is needed for your account, prescriptions, or lab work',
  'profile.contact.method.a11y.hint': 'Choose how you would like to be contacted',
  'profile.contact.method.a11y.label': 'Select preferred contact method',
  'profile.contact.preferences': 'Contact Preferences',
  'profile.contact.preferences.updated': 'Contact preference updated successfully',
  'profile.contact.save.a11y.hint': 'Press to save your contact method preferences',
  'profile.contact.save.a11y.label': 'Save contact preferences',
  'profile.contactMethod': 'Preference',
  'profile.contactPreference': 'Contact Preference',
  'profile.contactPreference.subtitle': 'How we reach you',
  'profile.contactSupport': 'Contact Support',
  'profile.contactSupport.subtitle': 'Resolve a specific issue or ask a question',
  'profile.demographics.bornAs.label': 'Born As',
  'profile.demographics.bornAs.placeholder': 'Select the sex assigned to you at birth',
  'profile.demographics.bornAs.select': 'Select your birth gender',
  'profile.demographics.ethnicity.label': 'Ethnicity',
  'profile.demographics.ethnicity.placeholder': 'Select your ethnicity',
  'profile.demographics.ethnicity.select': 'Select your ethnicity',
  'profile.demographics.gender.label': 'Gender',
  'profile.demographics.gender.placeholder': 'Select your gender identity',
  'profile.demographics.gender.select': 'Select your gender',
  'profile.demographics.height.add': 'Add your height',
  'profile.demographics.height.label': 'Height',
  'profile.demographics.height.placeholder': 'Select your height in feet and inches',
  'profile.demographics.height.invalid': 'Height is invalid',
  'profile.demographics.race.label': 'Race',
  'profile.demographics.race.min': 'You must choose at least 1 Race',
  'profile.demographics.race.placeholder': 'Select your race',
  'profile.demographics.race.select': 'Select your race',
  'profile.demographics.relationship.label': 'Relationship Status',
  'profile.demographics.relationship.placeholder': 'Select your relationship status',
  'profile.demographics.saved': 'Demographic and vitals information saved',
  'profile.demographics.sexuality.label': 'Sexuality',
  'profile.demographics.sexuality.placeholder': 'Select your sexual orientation',
  'profile.demographics.sexuality.select': 'Select your sexuality',
  'profile.demographics.title': 'Demographics and identity',
  'profile.demographics.weight.add': 'Add your weight',
  'profile.demographics.weight.label': 'Weight',
  'profile.demographics.weight.placeholder': 'Enter your weight in pounds',
  'profile.demographics.weight.unit': 'lbs',
  'profile.error.emailInUse': 'Email is already in use',
  'profile.error.phoneInUse': 'Phone is already in use',
  'profile.faq.subtitle': 'Find answers about how QCare+ works',
  'profile.faq.title': 'F.A.Q.',
  'profile.faq.header.title': "We're here to help",
  'profile.faq.header.description': 'Have questions? Browse our FAQ below or reach out to our support team.',
  // General Section
  'profile.faq.section.general': 'General',
  'profile.faq.section.general.a11y': 'General section',
  'profile.faq.general.prep.question': 'What is PrEP and how does it work?',
  'profile.faq.general.prep.answer':
    'Watch this educational video about HIV PrEP (Pre-Exposure Prophylaxis). Learn about how PrEP works, dosing instructions, side effects, lab testing requirements, and what to expect in your care journey with Q Care Plus.',
  'profile.faq.general.prep.video.title': 'Educational Video: Understanding HIV PrEP and Your Care',
  // Account Section
  'profile.faq.section.account': 'Account',
  'profile.faq.section.account.a11y': 'Account section',
  'profile.faq.account.photoId.question': 'How do I upload my photo ID?',
  'profile.faq.account.photoId.answer':
    "Navigate to your Profile section to upload your photo ID. We accept driver's license, passport, or work/school ID that shows your name and photo.",
  'profile.faq.account.photoId.button': 'Upload ID',
  'profile.faq.account.photoId.button.a11y.label': 'Go to photo ID upload section',
  'profile.faq.account.photoId.button.a11y.hint': 'Press to navigate to photo ID upload',
  'profile.faq.account.restartCare.question': 'How can I restart my care?',
  'profile.faq.account.restartCare.answer':
    "If you've fallen out of care and would like to restart services, please contact our support team.",
  'profile.faq.account.restartCare.button': 'Contact Support',
  'profile.faq.account.restartCare.button.a11y.label': 'Contact support team',
  'profile.faq.account.restartCare.button.a11y.hint': 'Press to send a message to support',
  // Testing Section
  'profile.faq.section.testing': 'Testing',
  'profile.faq.section.testing.a11y': 'Testing section',
  'profile.faq.testing.time.question': 'How long does in-person testing take?',
  'profile.faq.testing.time.answer':
    'A typical in-person lab appointment takes about 15 minutes once you arrive at the clinic. There are over 2,200 locations across the country where you can go for testing.',
  'profile.faq.testing.results.question': 'How fast will I get my test results?',
  'profile.faq.testing.results.answer':
    'It generally takes 5-7 business days for the lab to process your test kit. Your provider will then review your results and contact you to discuss any concerns.',
  'profile.faq.testing.results.button': 'View Lab Results',
  'profile.faq.testing.results.button.a11y.label': 'View lab results',
  'profile.faq.testing.externalLabs.question': 'How can I share lab results from other sources?',
  'profile.faq.testing.externalLabs.answer':
    'You can upload external lab results in the Lab Work section. Once uploaded, your provider will review them within 24-48 hours.',
  'profile.faq.testing.externalLabs.button': 'Upload Lab Results',
  'profile.faq.testing.externalLabs.button.a11y.label': 'Go to lab work section',
  // Lab Collection Instructions (Ash)
  'profile.faq.testing.ash.section': 'Ash Lab Collection Instructions',
  'profile.faq.testing.ash.section.a11y': 'Ash lab collection instructions section',
  'profile.faq.testing.ash.driedBlood.question': 'How do I collect a dried blood spot sample?',
  'profile.faq.testing.ash.driedBlood.answer':
    'Watch this instructional video to learn how to properly collect a dried blood spot sample using your at-home testing kit.',
  'profile.faq.testing.ash.driedBlood.video.title': 'Dried Blood Spot Collection Instructions',
  'profile.faq.testing.ash.urine.question': 'How do I collect a urine sample?',
  'profile.faq.testing.ash.urine.answer':
    'Watch this instructional video to learn how to properly collect a urine sample using your at-home testing kit.',
  'profile.faq.testing.ash.urine.video.title': 'Urine Sample Collection Instructions',
  'profile.faq.testing.ash.oralSwab.question': 'How do I collect an oral swab sample?',
  'profile.faq.testing.ash.oralSwab.answer':
    'Watch this instructional video to learn how to properly collect an oral swab sample using your at-home testing kit.',
  'profile.faq.testing.ash.oralSwab.video.title': 'Oral Swab Collection Instructions',
  'profile.faq.testing.ash.rectalSwab.question': 'How do I collect a rectal swab sample?',
  'profile.faq.testing.ash.rectalSwab.answer':
    'Watch this instructional video to learn how to properly collect a rectal swab sample using your at-home testing kit.',
  'profile.faq.testing.ash.rectalSwab.video.title': 'Rectal Swab Collection Instructions',
  // Lab Collection Instructions (MTL)
  'profile.faq.testing.mtl.section': 'At-Home Lab Collection Instructions',
  'profile.faq.testing.mtl.section.a11y': 'At-Home Lab Collection Instructions',
  'profile.faq.testing.mtl.bloodSpot.title': 'Blood spot collection',
  'profile.faq.testing.mtl.bloodSpot.description': 'Blood collection card',
  'profile.faq.testing.mtl.bloodSpot.before':
    'Before you start: Read all steps first. This kit is for single use only. Do not use it if the packaging or contents are damaged.',
  'profile.faq.testing.mtl.bloodSpot.step.1.title': 'Prepare the card and your hands',
  'profile.faq.testing.mtl.bloodSpot.step.1.body':
    'Write your name, date of birth, and collection date on the card. Use MM/DD/YYYY. Open the flap without touching the collection paper. Wash your hands in warm water for at least 30 seconds, then shake them vigorously for 15 seconds.',
  'profile.faq.testing.mtl.bloodSpot.step.1.image.1.alt': 'Hands being washed with soap.',
  'profile.faq.testing.mtl.bloodSpot.step.2.title': 'Clean and prick your fingertip',
  'profile.faq.testing.mtl.bloodSpot.step.2.body':
    'Clean a fingertip with the alcohol pad. The kit recommends the middle or ring finger on your non-dominant hand. Twist the cap off the lancet. Press the small tip firmly against the side of your fingertip, near the end, until it clicks. Use the lancet only once.',
  'profile.faq.testing.mtl.bloodSpot.step.2.image.1.alt': 'Lancet positioned against the side of a fingertip.',
  'profile.faq.testing.mtl.bloodSpot.step.3.title': 'Fill every circle',
  'profile.faq.testing.mtl.bloodSpot.step.3.body':
    'Keep your hand near waist level. Massage your finger from base to tip. Let a blood drop touch the center of each circle. Do not touch the paper with your finger. Fill every circle completely so the blood soaks through the paper. It is okay to go slightly beyond the outline.',
  'profile.faq.testing.mtl.bloodSpot.step.3.image.1.alt': 'A drop of blood touching a circle on the collection card.',
  'profile.faq.testing.mtl.bloodSpot.step.3.image.2.alt':
    'Correct: all circles filled with blood. Incorrect: incomplete or uneven blood spots.',
  'profile.faq.testing.mtl.bloodSpot.step.4.title': 'Let the card dry',
  'profile.faq.testing.mtl.bloodSpot.step.4.body':
    'Lay the card flat and let it air-dry for at least 30 minutes. Do not use heat or blow on it. Wait until it is fully dry before packing it.',
  'profile.faq.testing.mtl.bloodSpot.step.4.image.1.alt': 'Blood collection card with its flap open.',
  'profile.faq.testing.mtl.bloodSpot.step.5.title': 'Seal the sample bag',
  'profile.faq.testing.mtl.bloodSpot.step.5.body':
    'Tuck the card flap closed. Put the dried card and the desiccant pack (the drying packet) into their own biohazard specimen bag. Seal it completely. Follow the separate Pack and return your kit guide.',
  'profile.faq.testing.mtl.bloodSpot.step.5.image.1.alt': 'Separate biohazard specimen bag for the blood card.',
  'profile.faq.testing.mtl.urineFillLine.title': 'Urine collection',
  'profile.faq.testing.mtl.urineFillLine.description': 'Orange “URINE / Orina” label · Tube with fill lines',
  'profile.faq.testing.mtl.urineFillLine.before':
    'Before you start: Single-use kit; do not use damaged packaging or contents. No alcohol on collection day. Do not collect during menstruation or within 24 hours after sexual intercourse. Do not pour out preservative liquid.',
  'profile.faq.testing.mtl.urineFillLine.step.1.title': 'Wait before collecting',
  'profile.faq.testing.mtl.urineFillLine.step.1.body':
    'Do not urinate for at least 1 hour before collection. First-morning urine is preferred.',
  'profile.faq.testing.mtl.urineFillLine.step.1.image.1.alt': 'Clock showing a one-hour wait before urine collection.',
  'profile.faq.testing.mtl.urineFillLine.step.2.title': 'Collect urine in the cup',
  'profile.faq.testing.mtl.urineFillLine.step.2.body':
    'Urinate directly into the supplied cup until it is about one-third to one-half full.',
  'profile.faq.testing.mtl.urineFillLine.step.2.image.1.alt': 'Urine collected in the supplied cup.',
  'profile.faq.testing.mtl.urineFillLine.step.3.title': 'Transfer urine and check the fill lines',
  'profile.faq.testing.mtl.urineFillLine.step.3.body':
    'Use the pipette (the small dropper) to transfer urine into the tube or tubes with the orange “URINE / Orina” label. Fill until the combined liquid is between the marked lines. Do not exceed the maximum line; the sheet says an overfilled sample will be rejected.',
  'profile.faq.testing.mtl.urineFillLine.step.3.image.1.alt': 'Pipette transferring urine into the tube with the fill lines marked.',
  'profile.faq.testing.mtl.urineFillLine.step.4.title': 'Close and label each tube',
  'profile.faq.testing.mtl.urineFillLine.step.4.body':
    'Close each tube evenly and tightly. Write your date of birth (DOB) and today’s collection date (Coll. Date / Coll. DT) on each tube in MM/DD/YYYY format. Include leading zeros.',
  'profile.faq.testing.mtl.urineFillLine.step.4.image.1.alt':
    'Orange URINE / Orina tube label with fields for date of birth and collection date.',
  'profile.faq.testing.mtl.urineFillLine.step.5.title': 'Seal the sample bag',
  'profile.faq.testing.mtl.urineFillLine.step.5.body':
    'Put each tube in its own biohazard specimen bag with the absorbent pad. Seal the bag completely, then follow Pack and return your kit.',
  'profile.faq.testing.mtl.urineFillLine.step.5.image.1.alt': 'Urine tube inside a biohazard specimen bag.',
  'profile.faq.testing.mtl.neatUrine.title': 'Neat urine collection',
  'profile.faq.testing.mtl.neatUrine.description': 'Use only with the “Neat Urine Collection Kit” insert',
  'profile.faq.testing.mtl.neatUrine.before':
    'Before you start: Single-use kit; do not use damaged packaging or contents. Do not collect during menstruation or within 24 hours after sexual intercourse. Do not pour out any preservative liquid that is present.',
  'profile.faq.testing.mtl.neatUrine.step.1.title': 'Wait before collecting',
  'profile.faq.testing.mtl.neatUrine.step.1.body':
    'Do not urinate for at least 1 hour before collection. First-morning urine is preferred.',
  'profile.faq.testing.mtl.neatUrine.step.1.image.1.alt': 'Clock showing a one-hour wait before urine collection.',
  'profile.faq.testing.mtl.neatUrine.step.2.title': 'Collect urine in the cup',
  'profile.faq.testing.mtl.neatUrine.step.2.body':
    'Urinate directly into the supplied cup until it is about one-third to one-half full, as shown in the Neat Urine sheet.',
  'profile.faq.testing.mtl.neatUrine.step.2.image.1.alt': 'Urine collected in the supplied cup.',
  'profile.faq.testing.mtl.neatUrine.step.3.title': 'Transfer urine into the tube',
  'profile.faq.testing.mtl.neatUrine.step.3.body':
    'Use the pipette (the small dropper) to move urine into the Neat Urine collection tube. Fill it to 10–13 mL, approximately three-quarters full, as specified in this kit’s sheet. Do not use this amount for the orange-label kit.',
  'profile.faq.testing.mtl.neatUrine.step.3.image.1.alt': 'Pipette transferring urine into the Neat Urine collection tube.',
  'profile.faq.testing.mtl.neatUrine.step.4.title': 'Close and label the tube',
  'profile.faq.testing.mtl.neatUrine.step.4.body':
    'Close the tube evenly and tightly. Write your date of birth (DOB) and today’s collection date (Coll. DT) on the tube using MM/DD/YYYY. Include leading zeros.',
  'profile.faq.testing.mtl.neatUrine.step.4.image.1.alt': 'Neat Urine tube label with fields for date of birth and collection date.',
  'profile.faq.testing.mtl.neatUrine.step.5.title': 'Seal the sample bag',
  'profile.faq.testing.mtl.neatUrine.step.5.body':
    'Place the tube in its biohazard specimen bag with the absorbent pad. Seal the bag completely, then follow Pack and return your kit.',
  'profile.faq.testing.mtl.neatUrine.step.5.image.1.alt': 'Neat Urine tube inside a biohazard specimen bag.',
  'profile.faq.testing.mtl.packReturn.title': 'Pack and return your kit',
  'profile.faq.testing.mtl.packReturn.description': 'Use after collecting all required samples',
  'profile.faq.testing.mtl.packReturn.before':
    'Before you start: Use the collection instructions for each sample in your kit first. Pack only one specimen per bag. Keep the dried blood card in its own bag with its desiccant pack.',
  'profile.faq.testing.mtl.packReturn.step.1.title': 'Check every label',
  'profile.faq.testing.mtl.packReturn.step.1.body':
    'Each tube needs your date of birth and collection date. The blood card also needs your name. Write dates as MM/DD/YYYY, with leading zeros, for example 02/05/2027.',
  'profile.faq.testing.mtl.packReturn.step.1.image.1.alt': 'Tube label showing the date of birth and collection date fields.',
  'profile.faq.testing.mtl.packReturn.step.2.title': 'Check the blood card, if used',
  'profile.faq.testing.mtl.packReturn.step.2.body':
    'Every circle should be completely filled, with blood soaked through the paper. The card must have air-dried for at least 30 minutes and be fully dry before packing.',
  'profile.faq.testing.mtl.packReturn.step.2.image.1.alt':
    'Correct: all blood card circles filled. Incorrect: incomplete or uneven blood spots.',
  'profile.faq.testing.mtl.packReturn.step.3.title': 'Seal each specimen separately',
  'profile.faq.testing.mtl.packReturn.step.3.body':
    'Close all tube lids evenly and tightly. Put each tube in its own biohazard bag with the absorbent pad. Put the dried blood card in a separate bag with the desiccant pack. Seal every bag completely.',
  'profile.faq.testing.mtl.packReturn.step.3.image.1.alt': 'Biohazard specimen bag for an individual sample.',
  'profile.faq.testing.mtl.packReturn.step.4.title': 'Close the return package',
  'profile.faq.testing.mtl.packReturn.step.4.body':
    'Place all sealed specimen bags in the supplied box. Put the box inside the prepaid return-shipping bag and seal it fully. Make sure all samples required for your tests are included.',
  'profile.faq.testing.mtl.packReturn.step.5.title': 'Drop off the kit',
  'profile.faq.testing.mtl.packReturn.step.5.body':
    'Take the sealed kit to a U.S. Postal Service (USPS) location (hand it to a post office clerk). The brochure says you will receive an email when the lab receives the kit and another notification when results are ready. It does not give a turnaround time.',
  'profile.faq.testing.mtl.throatOralSwab.title': 'Throat (oral) swab',
  'profile.faq.testing.mtl.throatOralSwab.description': 'Blue “ORAL / Bucal” label',
  'profile.faq.testing.mtl.throatOralSwab.before':
    'Before you start: Single-use kit; do not use damaged materials. For 30 minutes before collection, do not eat, drink, smoke, or brush your teeth. Do not drink alcohol on collection day. Do not empty any liquid preservative from the tube.',
  'profile.faq.testing.mtl.throatOralSwab.step.1.title': 'Identify the swab and tube',
  'profile.faq.testing.mtl.throatOralSwab.step.1.body':
    'Use the oral swab and the tube with the blue “ORAL / Bucal” label. Hold the swab between the faint score line (if present) and the cotton tip.',
  'profile.faq.testing.mtl.throatOralSwab.step.1.image.1.alt': 'Oral swab beside the tube with the blue ORAL / Bucal label.',
  'profile.faq.testing.mtl.throatOralSwab.step.2.title': 'Swab the back of your throat',
  'profile.faq.testing.mtl.throatOralSwab.step.2.body':
    'Put the swab in your mouth and rub its tip against the back of your throat for 10 seconds.',
  'profile.faq.testing.mtl.throatOralSwab.step.2.image.1.alt': 'Swab tip positioned against the back of the throat.',
  'profile.faq.testing.mtl.throatOralSwab.step.3.title': 'Put the swab in the tube',
  'profile.faq.testing.mtl.throatOralSwab.step.3.body':
    'Carefully remove the swab and place it in the oral collection tube. Bend it against the tube’s edge to break it at the marked line.',
  'profile.faq.testing.mtl.throatOralSwab.step.3.image.1.alt': 'Swab shaft being broken against the edge of the oral collection tube.',
  'profile.faq.testing.mtl.throatOralSwab.step.4.title': 'Close and label the tube',
  'profile.faq.testing.mtl.throatOralSwab.step.4.body':
    'Close the lid evenly and tightly. Write your date of birth (DOB) and today’s collection date (Coll. Date / Coll. DT) using MM/DD/YYYY, including leading zeros.',
  'profile.faq.testing.mtl.throatOralSwab.step.4.image.1.alt': 'Blue ORAL / Bucal tube label with the date of birth field.',
  'profile.faq.testing.mtl.throatOralSwab.step.4.image.2.alt': 'Blue ORAL / Bucal tube label with the collection date field.',
  'profile.faq.testing.mtl.throatOralSwab.step.5.title': 'Seal the sample bag',
  'profile.faq.testing.mtl.throatOralSwab.step.5.body':
    'Put the tube in its biohazard specimen bag with the absorbent pad. Seal it completely, then follow Pack and return your kit.',
  'profile.faq.testing.mtl.throatOralSwab.step.5.image.1.alt': 'Oral swab tube inside its biohazard specimen bag.',
  'profile.faq.testing.mtl.rectalAnalSwab.title': 'Rectal (anal) swab',
  'profile.faq.testing.mtl.rectalAnalSwab.description': 'Green “ANAL” label',
  'profile.faq.testing.mtl.rectalAnalSwab.before':
    'Before you start: Read all steps first. Single-use kit; do not use damaged packaging or contents. Do not drink alcohol for 1 hour before collection. Do not empty any liquid preservative from the tube.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.1.title': 'Identify the swab and tube',
  'profile.faq.testing.mtl.rectalAnalSwab.step.1.body':
    'Use the anal swab and the tube with the green “ANAL” label. Hold the swab between the faint score line (if present) and the cotton tip.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.1.image.1.alt': 'Anal swab beside the tube with the green ANAL label.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.2.title': 'Collect the sample',
  'profile.faq.testing.mtl.rectalAnalSwab.step.2.body':
    'Gently insert the swab about 1–2 inches (3–5 cm) into the anal canal. Gently rotate it for 5–10 seconds.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.3.title': 'Put the swab in the tube',
  'profile.faq.testing.mtl.rectalAnalSwab.step.3.body':
    'Carefully remove the swab and place it in the anal collection tube. Bend it against the tube’s edge to break it at the marked line.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.3.image.1.alt': 'Swab shaft being broken against the edge of the anal collection tube.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.4.title': 'Close and label the tube',
  'profile.faq.testing.mtl.rectalAnalSwab.step.4.body':
    'Close the lid evenly and tightly. Write your date of birth (DOB) and today’s collection date (Coll. DT) using MM/DD/YYYY, including leading zeros.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.4.image.1.alt': 'Green ANAL tube label with the date of birth field.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.4.image.2.alt': 'Green ANAL tube label with the collection date field.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.5.title': 'Seal the sample bag',
  'profile.faq.testing.mtl.rectalAnalSwab.step.5.body':
    'Put the tube in its biohazard specimen bag with the absorbent pad. Seal it completely, then follow Pack and return your kit.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.5.image.1.alt': 'Anal swab tube inside its biohazard specimen bag.',
  // Pharmacy Section
  'profile.faq.section.pharmacy': 'Pharmacy',
  'profile.faq.section.pharmacy.a11y': 'Pharmacy section',
  'profile.faq.pharmacy.status.question': 'How do I find out my prescription status?',
  'profile.faq.pharmacy.status.answer':
    'To check on the status of your prescription, navigate to the Prescriptions section and select "View details" for the specific prescription you\'re interested in.',
  'profile.faq.pharmacy.status.button': 'View Prescriptions',
  'profile.faq.pharmacy.status.button.a11y.label': 'View prescriptions',
  'profile.faq.pharmacy.contact.question': 'What is the contact information for my pharmacy?',
  'profile.faq.pharmacy.contact.noPharmacy':
    'We do not have a pharmacy on file for you. Please contact your provider for more information.',
  'profile.faq.pharmacy.contact.call': 'Call {pharmacyName}',
  // Medical Section
  'profile.faq.section.medical': 'Medical',
  'profile.faq.section.medical.a11y': 'Medical section',
  'profile.faq.medical.pep.question': "How can I meet with a provider if I've been exposed to HIV?",
  'profile.faq.medical.pep.answer':
    "If you've been potentially exposed to HIV within the past 72 hours, you can meet with your provider to determine if PEP is right for you.",
  'profile.faq.medical.pep.button': 'Schedule PEP Appointment',
  'profile.faq.medical.pep.button.a11y.label': 'Schedule PEP appointment',
  // Support Section
  'profile.faq.support.section.a11y': 'Support section',
  'profile.faq.support.title': 'Still have questions?',
  'profile.faq.support.description': 'Our support team is here to assist you with any questions or concerns you may have.',
  'profile.faq.support.button': 'Contact Support',
  'profile.faq.support.button.a11y.label': 'Contact support team',
  'profile.firstName': 'First Name',
  'profile.firstName.placeholder': 'Enter your first name',
  'profile.form.a11y.label': 'Personal information form',
  'profile.healthAssistance.subtitle': 'Check funding eligibility',
  'profile.healthAssistance.title': 'Health Assistance',
  'profile.primaryCareProvider.title': 'Primary Care Provider',
  'profile.primaryCareProvider.subtitle': 'Your primary care provider info',
  'profile.healthHistory.subtitle': 'Medical records',
  'profile.healthHistory.title': 'Health History',
  'profile.help': 'Help',
  'profile.vitals.description': 'Track your vital signs over time',
  'profile.vitals.subtitle': 'Weight, height, blood pressure, heart rate',
  'profile.vitals.title': 'Vitals',
  'profile.insurance.subtitle': 'Insurance details',
  'profile.insurance.title': 'Insurance',
  'profile.insurances.add': 'Add new insurance',
  'profile.insurances.add.hint': 'Press to add a new insurance card',
  'profile.insurances.title': 'Insurances',
  'profile.language': 'Language',
  'profile.language.english': 'English',
  'profile.language.label': 'Language',
  'profile.language.placeholder': 'Select your preferred language',
  'profile.language.spanish': 'Español',
  'profile.language.updated': 'Language preference updated',
  'profile.lastName': 'Last Name',
  'profile.lastName.placeholder': 'Enter your last name',
  'profile.logout.message': 'Are you sure you want to logout?',
  'profile.logout.title': 'Logout',
  'profile.missing.emailVerification': 'Please verify your email',
  'profile.missing.phoneVerification': 'Please verify your phone number',
  'profile.missing.language': 'Select your preferred language',
  'profile.missing.profilePicture': 'Add a profile photo',
  'profile.missing.firstName': 'Add a first name',
  'profile.missing.lastName': 'Add a last name',
  'profile.missing.email': 'Add an email address',
  'profile.missing.dob': 'Add a birthdate',
  'profile.missing.photoId': 'Upload image of Government ID',
  'profile.missing.contactMethod': 'Please choose a contact preference',
  'profile.nickname': 'Chosen Name',
  'profile.nickname.placeholder': 'Enter your chosen name',
  'profile.notifications': 'Notifications',
  'profile.notifications.description':
    'We only send notifications to you regarding important information about your account and healthcare',
  'profile.notifications.opted_out_start_description':
    'You have opted out from receiving text messages. To start receiving text messages, send START to {phone}.',
  'profile.notifications.opted_out_description':
    'You have opted out from receiving text messages. Enable text notifications to receive important updates about your care.',
  'profile.notifications.opted_out_email_info': 'While opted out, we will send email messages for information about your account and care.',
  'profile.notifications.opt_in_button': 'Enable Text Notifications',
  'profile.notifications.opt_in_success': "You're subscribed to SMS alerts",
  'profile.notifications.opt_in.a11y.label': 'Enable text notifications',
  'profile.notifications.opt_in.a11y.hint': 'Tap to opt in to receive text message notifications',
  'profile.notifications.send_start_button': 'Send START',
  'profile.notifications.send_start.a11y.label': 'Send START by text message',
  'profile.notifications.send_start.a11y.hint': 'Opens your messages app to text START and turn text notifications back on',
  'profile.notifications.subtitle': 'Alert preferences',
  'profile.notifications.updated': 'Notification preferences updated successfully',
  'profile.personalInfo.saved': 'Personal information saved',
  'profile.photoId': 'Photo of Government Issued ID',
  'profile.photoId.title': 'Photo ID',
  'profile.photoId.upload': 'Upload image of Government ID',
  'profile.preferredCommunication': 'Send notifications to',
  'profile.profile.subtitle': 'Personal information',
  'profile.profilePicture': 'Profile Photo',
  'profile.profilePicture.a11y.add': 'Add profile photo',
  'profile.profilePicture.a11y.change': 'Change profile photo',
  'profile.profilePicture.a11y.hint': 'Opens photo selection options',
  'profile.profilePicture.add': 'Tap to add photo',
  'profile.profilePicture.change': 'Tap to change photo',
  'profile.profilePicture.edit.a11y': 'Edit profile photo',
  'profile.profilePicture.remove.toast': 'Profile picture removed',
  'profile.profilePicture.update.toast': 'Profile picture updated',
  'profile.progressItems.aboutMe.description': 'Fill in demographics and identity',
  'profile.progressItems.aboutMe.label': 'About Me',
  'profile.progressItems.contactPreference.description': 'Set preferred contact method',
  'profile.progressItems.contactPreference.label': 'Contact Preference',
  'profile.progressItems.healthAssistance.description': 'Complete health assistance information',
  'profile.progressItems.healthAssistance.label': 'Health Assistance',
  'profile.progressItems.insurance.description': 'Add insurance details',
  'profile.progressItems.insurance.label': 'Insurance',
  'profile.progressItems.photoId.description': 'Upload photo identification',
  'profile.progressItems.photoId.label': 'Photo ID',
  'profile.progressItems.profile.description': 'Complete personal information',
  'profile.progressItems.profile.label': 'Profile',
  'profile.progressItems.vitals.description': 'Add vitals information',
  'profile.progressItems.vitals.label': 'Vitals',
  'profile.pronouns': 'Pronouns',
  'profile.pronouns.placeholder': 'Select your pronouns',
  'profile.save.a11y.hint': 'Press to save your profile information',
  'profile.save.a11y.label': 'Save profile changes',
  'profile.settings': 'Settings',
  'profile.support': 'Support',
  'profile.theme': 'Theme',
  'profile.theme.subtitle': 'Tap to cycle through options',
  'profile.title': 'Profile',
  'profile.verification.email.action': 'Verify',
  'profile.verification.email.description': 'Verify email for account recovery',
  'profile.verification.email.sent': 'We sent you an email with a link to verify your email address',
  'profile.verification.email.title': 'Email Verification',
  'profile.verification.phone.action': 'Verify',
  'profile.verification.phone.description': 'Secure your account with SMS code verification',
  'profile.verification.phone.title': 'Phone Verification',
  'profile.verify.email': 'Verify your email',
  'profile.verify.phone': 'Verify your phone',

  // ID Verification
  'photo_id.after_appointment.description':
    "You have successfully scheduled your appointment!\n\nPlease upload your government-issued ID. Ensure your name, date of birth, and picture are visible.\n\nIf you're unable to upload it now, please have it readily available for the provider at the beginning of your appointment.",
  'photo_id.description':
    "For identification purposes, we need a photo of your government-issued ID such as a driver's license, passport, or state ID card.",
  'photo_id.error.upload.message': 'There was a problem uploading your photo ID. Please try again.',
  'photo_id.error.upload.title': 'Upload Error',
  'photo_id.no_photo': 'No photo ID uploaded',
  'photo_id.success.message': 'Your photo ID has been uploaded successfully.',
  'photo_id.success.title': 'Success',
  'photo_id.title': 'Upload ID',
  'photo_id.footer': "Acceptable photo IDs are: driver's license, passport, or work/school ID that displays both your name and photo.",
  'photo_id.button.skip': 'Skip for now',
  'photo_id.tip':
    'Tip: Uploading your photo ID now will save time during your appointment. If you skip this step, your provider will need to screenshot your ID at the beginning of your visit.',

  // Address Management
  'address.cbo.a11y.label': 'Community partner selection dialog',
  'address.cbo.a11y.select': 'Select {name}',
  'address.cbo.description': 'Your new location requires joining a new community partner that serves your area.',
  'address.cbo.title': 'Choose Community Partner',
  'address.county.a11y.label': 'County selection dialog',
  'address.county.a11y.select': 'Select {county} county',
  'address.county.description': 'Your new location requires joining a new community partner that serves your area.',
  'address.county.title': 'Which county do you live in',
  'address.edit.a11y.screen': 'Address form screen',
  'address.edit.a11y.submit': 'Save address information',
  'address.edit.homeAddress.title': 'Home Address',
  'address.edit.notify.error.description': 'Sorry to see you go!',
  'address.edit.notify.success': 'Information saved',
  'address.edit.notify.success.description': "We'll notify you when a community partner becomes available in your area",
  'address.edit.shippingAddress.title': 'Shipping Address',
  'address.edit.success': 'Address information saved',
  'address.edit.verifyConfirm': 'Verify & Confirm',
  'address.timezone.title': 'Confirm Your Timezone',
  'address.timezone.description.change': 'Your address change may require updating your timezone. Please confirm or select a new timezone.',
  'address.timezone.description.select': 'Please select your timezone to ensure accurate appointment scheduling.',
  'address.timezone.current.label': 'Current Selection',
  'address.timezone.select.available': 'Available Timezones',
  'address.timezone.select.label': 'Select Timezone',
  'address.insurance.a11y.label': 'Insurance check dialog',
  'address.noinsurance.a11y.label': 'Insurance required dialog',
  'address.noinsurance.title': 'Insurance Required',
  'address.medicare.a11y.label': 'Medi-Cal check dialog',
  'address.nocbo.a11y.label': 'No community partner available dialog',
  'address.nocbo.contact': 'Contact Customer Support',
  'address.nocbo.title': 'No Community Partner Available',

  // Address Verification
  'addressVerification.title': 'Restart Care',

  // General Health
  'health.gridItem.a11y.label': '{title}. {description}',
  'health.section.care': 'Your care',
  'health.section.services': 'Health services',
  'health.section.support': 'Support',
  'health.screen.a11y.healthServices': 'Health services grid',
  'health.screen.a11y.label': 'Health services main screen',
  'health.screen.a11y.quickActions': 'Quick access health actions',
  'health.screen.a11y.support': 'Support and assistance options',
  'health.screen.appointments.description': 'View and manage your schedule',
  'health.screen.appointments.pageDescription':
    'Schedule visits, join video calls, and keep track of all your healthcare appointments in one convenient place.',
  'health.screen.appointments.title': 'Appointments',
  'health.screen.assessments.description': 'Complete health checks',
  'health.screen.assessments.pageDescription':
    'Complete quick health check-ins that help your care team provide you with personalized, effective treatment.',
  'health.screen.assessments.title': 'Assessments',
  'health.screen.documents.description': 'View your medical records',
  'health.screen.documents.pageDescription':
    'Access all your important medical records and documents in one secure, organized digital library.',
  'health.screen.documents.title': 'Documents',
  'health.screen.healthHistory.description': 'Review your past conditions',
  'health.screen.healthHistory.title': 'Health History',
  'health.screen.labResults.description': 'Access your test results',
  'health.screen.labResults.title': 'Lab Results',
  'health.screen.labs.pageDescription':
    'Track your health journey with detailed lab results and insights that help guide your care decisions.',
  'health.screen.needAssistance.description': 'Our support team is here to help',
  'health.screen.needAssistance.title': 'Need Assistance?',
  'health.screen.medReminders.title': 'Med Reminders',
  'health.screen.medReminders.description': 'Set reminders to take your medication',
  'health.screen.prescriptions.description': 'Manage your medications',
  'health.screen.prescriptions.pageDescription':
    'Manage your medications with ease - track refills, delivery status, and stay on top of your health routine.',
  'health.screen.prescriptions.title': 'Prescriptions',

  // Health History
  'healthHistory.description':
    "Please take a moment to review your Health History as displayed below. If there's any new information that isn't currently shown, feel free to add it.",
  'healthHistory.empty': '{type}',
  'healthHistory.empty.instructions': 'Nothing added yet. Add one, or tick the box if you have none.',
  'healthHistory.empty.noKnown': "I don't have any known {type}",
  'healthHistory.empty.requiresAppointment': 'Booking an appointment is required before your Health History can be completed',
  'healthHistory.empty.requiresProvider': 'You need an assigned provider before updating your Health History',
  'healthHistory.form.dosage': 'Dosage',
  'healthHistory.form.frequency': 'Frequency',
  'healthHistory.form.reasonForMedication': 'Reason for medication',
  'healthHistory.form.reasonForMedication.short': 'Reason',
  'healthHistory.form.name': 'Name',
  'healthHistory.form.onsetYear': 'Year',
  'healthHistory.form.reaction': 'Reaction',
  'healthHistory.form.selectYear': 'Select Year',
  'healthHistory.form.severity': 'Severity',
  'healthHistory.form.year': 'Year',
  'healthHistory.item.dosage': 'Dosage: {dosage}',
  'healthHistory.item.frequency': 'Frequency: {frequency}',
  'healthHistory.item.reasonForMedication': 'Reason: {reason}',
  'healthHistory.item.na': 'n/a',
  'healthHistory.item.onset': 'Onset: {year}',
  'healthHistory.item.reaction': 'Reaction: {reaction}',
  'healthHistory.item.severity': 'Severity: {severity}',
  'healthHistory.item.year': 'Year: {year}',
  'healthHistory.lastUpdated': 'Last updated:',
  'healthHistory.medications.instructionText':
    "Please list any and all prescription medications, over the counter medications, and supplements you're currently taking. For each medication or supplement added, please be sure to list the reason for use in the conditions section.",
  'healthHistory.singular.allergy': 'Allergy',
  'healthHistory.singular.condition': 'Condition',
  'healthHistory.singular.immunization': 'Immunization',
  'healthHistory.singular.medication': 'Medication',
  'healthHistory.singular.surgery': 'Surgery',
  'healthHistory.tabs.allergies': 'Allergies',
  'healthHistory.tabs.conditions': 'Conditions',
  'healthHistory.tabs.immunizations': 'Immunizations',
  'healthHistory.tabs.medications': 'Medications',
  'healthHistory.tabs.surgeries': 'Surgeries',
  'healthHistory.update.addNew': 'Add new {type}',
  'healthHistory.update.disclaimer':
    'Once the health history has been updated, any further changes can only be made by your healthcare provider',
  'healthHistory.update.saveSuccess': '{type} information saved',

  // Vitals
  'vitals.bloodPressure.diastolic.label': 'Diastolic',
  'vitals.bloodPressure.diastolic.placeholder': '80',
  'vitals.bloodPressure.label': 'Blood Pressure',
  'vitals.bloodPressure.recordedAt.label': 'Date Recorded',
  'vitals.bloodPressure.systolic.label': 'Systolic',
  'vitals.bloodPressure.systolic.placeholder': '120',
  'vitals.bloodPressure.unit': 'mmHg',
  'vitals.bloodPressure.validation.systolicGreater': 'Systolic must be more than Diastolic',
  'vitals.empty.noRecorded': 'No {vital} recorded yet',
  'vitals.heartRate.label': 'Heart Rate',
  'vitals.heartRate.unit': 'bpm',
  'vitals.heartRate.recordedAt.label': 'Date Recorded',
  'vitals.height.label': 'Height',
  'vitals.height.unit': 'inches',
  'vitals.latest': 'Latest:',
  'vitals.weight.label': 'Weight',
  'vitals.weight.unit': 'lbs',
  'vitals.update.saveSuccess': '{type} saved successfully',

  // Health Assistance
  'healthAssistance.cbo.notAcceptingUninsured': 'CBO is not accepting uninsured patients',
  'healthAssistance.description': 'We use this information to determine if there are external programs to help fund your care',
  'healthAssistance.form.a11y.label': 'Financial assistance information form',
  'healthAssistance.gilead.label': 'Gilead AA#',
  'healthAssistance.gilead.placeholder': 'Gilead Advancing Access Number',
  'healthAssistance.household.invalid': 'Please enter a valid household size',
  'healthAssistance.household.label': 'Number of household members',
  'healthAssistance.household.placeholder': 'Number of household members',
  'healthAssistance.income.invalid': 'Please enter a valid income',
  'healthAssistance.income.label': 'Household Income',
  'healthAssistance.income.placeholder': 'Household Income',
  'healthAssistance.medicaid.label': 'Medicaid',
  'healthAssistance.save.a11y.hint': 'Press to save your financial assistance details',
  'healthAssistance.save.a11y.label': 'Save health assistance information',
  'healthAssistance.saved': 'Health Assistance information saved',
  'healthAssistance.screen.a11y.label': 'Health assistance form screen',
  'healthAssistance.social.invalid': 'Please enter valid SSN',
  'healthAssistance.social.label': 'Last 4 of Social Security Number',
  'healthAssistance.social.placeholder': 'Last 4 of SSN',
  'healthAssistance.title': 'Check funding eligibility',
  'healthAssistance.va.label': 'Veteran',

  // Primary Care Provider
  'primaryCareProvider.saved': 'Primary Care Provider information saved',
  'primaryCareProvider.save.a11y.label': 'Save primary care provider information',
  'primaryCareProvider.save.a11y.hint': 'Press to save your primary care provider details',
  'primaryCareProvider.clinic.label': 'Clinic',
  'primaryCareProvider.clinic.placeholder': 'Enter clinic name',
  'primaryCareProvider.name.label': 'Provider Name',
  'primaryCareProvider.name.placeholder': 'Enter provider name',
  'primaryCareProvider.address.label': 'Address',
  'primaryCareProvider.address.placeholder': 'Enter address',

  'treatments.noTreatments.description':
    'Unfortunately, there are no treatment options available for your account at this time. Please contact support for assistance.',
  'treatments.noTreatments.title': 'No Treatments Available',

  // Encounter Type Configurations (for appointment treatment selection)
  'encounter.default.title': 'Healthcare Service',
  'encounter.default.subtitle': 'Medical Care',
  'encounter.default.description': 'Professional healthcare services.',
  'encounter.default.overview': 'Professional healthcare services tailored to your needs.',
  'encounter.default.includes.1': 'Provider consultation',
  'encounter.default.includes.2': 'Personalized care plan',
  'encounter.default.includes.3': 'Ongoing support',
  'encounter.default.ideal': 'For individuals seeking quality healthcare services.',

  'encounter.prepInitial.title': 'PrEP & HIV Prevention',
  'encounter.prepInitial.subtitle': 'Get Started',
  'encounter.prepInitial.description':
    'Get PrEP to prevent HIV, plus HIV and STI testing every 3 months. You can also get DoxyPEP, ED medication, hair loss treatment, and birth control with your PrEP.',
  'encounter.prepInitial.overview':
    'Start your PrEP journey with an initial consultation. Our healthcare providers will assess your eligibility, review your medical history, and create a personalized prevention plan.',
  'encounter.prepInitial.includes.1': 'Initial health assessment',
  'encounter.prepInitial.includes.2': 'HIV/STI baseline testing',
  'encounter.prepInitial.includes.3': 'PrEP prescription if eligible',
  'encounter.prepInitial.includes.4': 'Education on medication adherence',
  'encounter.prepInitial.ideal': 'For individuals new to PrEP who want to start HIV prevention.',

  'encounter.prepFollowUp.title': 'PrEP Follow Up',
  'encounter.prepFollowUp.subtitle': 'Continue Your Care',
  'encounter.prepFollowUp.description': 'Regular check-in to monitor your PrEP treatment progress.',
  'encounter.prepFollowUp.overview':
    "Continue your PrEP care with a follow-up appointment. We'll review your health, address any concerns, and ensure your prevention plan is working effectively.",
  'encounter.prepFollowUp.includes.1': 'Health status review',
  'encounter.prepFollowUp.includes.2': 'Side effects assessment',
  'encounter.prepFollowUp.includes.3': 'Prescription renewal',
  'encounter.prepFollowUp.includes.4': 'Ongoing support',
  'encounter.prepFollowUp.ideal': 'For current PrEP patients needing a follow-up consultation.',

  'encounter.prepQuarterly.title': 'PrEP Quarterly',
  'encounter.prepQuarterly.subtitle': 'Quarterly Lab & Check-in',
  'encounter.prepQuarterly.description': 'Routine quarterly visit including lab testing.',
  'encounter.prepQuarterly.overview':
    'Your quarterly PrEP appointment includes required lab testing and a provider check-in to ensure continued effectiveness and health monitoring.',
  'encounter.prepQuarterly.includes.1': 'Quarterly HIV/STI testing',
  'encounter.prepQuarterly.includes.2': 'Kidney function monitoring',
  'encounter.prepQuarterly.includes.3': 'Prescription renewal',
  'encounter.prepQuarterly.includes.4': 'Prevention counseling',
  'encounter.prepQuarterly.ideal': 'For PrEP patients due for their quarterly labs and check-up.',

  'encounter.injectablePrEP.title': 'Injectable PrEP',
  'encounter.injectablePrEP.subtitle': 'Bi-monthly Injection',
  'encounter.injectablePrEP.description': 'HIV prevention with a convenient shot every two months.',
  'encounter.injectablePrEP.overview':
    'Injectable PrEP offers HIV prevention through convenient bi-monthly injections, eliminating the need for daily pills.',
  'encounter.injectablePrEP.includes.1': 'Professional administered injections',
  'encounter.injectablePrEP.includes.2': 'No daily pills required',
  'encounter.injectablePrEP.includes.3': 'Regular health monitoring',
  'encounter.injectablePrEP.includes.4': 'Appointment scheduling support',
  'encounter.injectablePrEP.ideal': 'Perfect for those who prefer not to take daily medication but want effective HIV prevention.',

  'encounter.pep.title': 'PEP',
  'encounter.pep.subtitle': 'Emergency Prevention',
  'encounter.pep.description': 'Post-exposure HIV prevention — start within 72 hours.',
  'encounter.pep.overview':
    'PEP (Post-Exposure Prophylaxis) is an emergency medication to prevent HIV after potential exposure. Time is critical — treatment must begin within 72 hours.',
  'encounter.pep.includes.1': 'Emergency medication access',
  'encounter.pep.includes.2': '28-day treatment course',
  'encounter.pep.includes.3': 'Follow-up HIV testing',
  'encounter.pep.includes.4': 'Transition to PrEP counseling',
  'encounter.pep.ideal': 'For individuals who may have been exposed to HIV within the last 72 hours.',

  'encounter.stiTesting.title': 'One-Time STI Testing',
  'encounter.stiTesting.subtitle': 'Get Tested',
  'encounter.stiTesting.description': 'Get tested for HIV and common STIs, including gonorrhea, chlamydia, and syphilis.',
  'encounter.stiTesting.overview': 'STI testing and prevention services to keep you informed about your sexual health.',
  'encounter.stiTesting.includes.1': 'Full STI panel testing',
  'encounter.stiTesting.includes.2': 'Professional consultation',
  'encounter.stiTesting.includes.3': 'Quick and confidential results',
  'encounter.stiTesting.includes.4': 'Prevention counseling',
  'encounter.stiTesting.ideal': 'Recommended for routine sexual health screening and prevention.',

  'encounter.stiFollowUp.title': 'STI Follow Up',
  'encounter.stiFollowUp.subtitle': 'Treatment & Support',
  'encounter.stiFollowUp.description': 'Follow-up care for STI treatment and monitoring.',
  'encounter.stiFollowUp.overview':
    'Follow-up appointment to monitor your STI treatment progress, ensure treatment effectiveness, and provide ongoing support.',
  'encounter.stiFollowUp.includes.1': 'Treatment effectiveness review',
  'encounter.stiFollowUp.includes.2': 'Additional testing if needed',
  'encounter.stiFollowUp.includes.3': 'Partner notification support',
  'encounter.stiFollowUp.includes.4': 'Prevention education',
  'encounter.stiFollowUp.ideal': 'For patients who need follow-up after STI diagnosis or treatment.',

  'encounter.hivEnrollment.title': 'HIV Enrollment',
  'encounter.hivEnrollment.subtitle': 'Start Your Care Journey',
  'encounter.hivEnrollment.description': 'Begin comprehensive HIV care and support services.',
  'encounter.hivEnrollment.overview':
    "Start your HIV care journey with our comprehensive enrollment process. We'll create a personalized care plan focused on your health and wellbeing.",
  'encounter.hivEnrollment.includes.1': 'Initial health assessment',
  'encounter.hivEnrollment.includes.2': 'Care plan development',
  'encounter.hivEnrollment.includes.3': 'Treatment education',
  'encounter.hivEnrollment.includes.4': 'Support services introduction',
  'encounter.hivEnrollment.ideal': 'For individuals newly diagnosed or transitioning to our HIV care services.',

  'encounter.hivInitial.title': 'Living with HIV',
  'encounter.hivInitial.subtitle': 'Get Care',
  'encounter.hivInitial.description': 'Start or continue HIV treatment and get ongoing HIV care.',
  'encounter.hivInitial.overview':
    'Your initial HIV care appointment includes a thorough health evaluation and the development of your personalized treatment plan.',
  'encounter.hivInitial.includes.1': 'Complete health evaluation',
  'encounter.hivInitial.includes.2': 'Baseline lab testing',
  'encounter.hivInitial.includes.3': 'Treatment plan discussion',
  'encounter.hivInitial.includes.4': 'Medication initiation if appropriate',
  'encounter.hivInitial.ideal': 'For new patients beginning HIV treatment and care.',

  'encounter.hivFollowUp.title': 'HIV Follow Up',
  'encounter.hivFollowUp.subtitle': 'Ongoing Care',
  'encounter.hivFollowUp.description': 'Regular follow-up to monitor your HIV care progress.',
  'encounter.hivFollowUp.overview':
    'Continue your HIV care with regular follow-up appointments to monitor your health, review treatment effectiveness, and adjust care as needed.',
  'encounter.hivFollowUp.includes.1': 'Viral load monitoring',
  'encounter.hivFollowUp.includes.2': 'CD4 count review',
  'encounter.hivFollowUp.includes.3': 'Medication management',
  'encounter.hivFollowUp.includes.4': 'Ongoing health support',
  'encounter.hivFollowUp.ideal': 'For patients on HIV treatment needing regular check-ups.',

  'encounter.hivAdherence.title': 'HIV Adherence',
  'encounter.hivAdherence.subtitle': 'Medication Support',
  'encounter.hivAdherence.description': 'Support session focused on medication adherence.',
  'encounter.hivAdherence.overview':
    'A focused session to support your medication adherence, address challenges, and help you maintain consistent treatment for optimal health outcomes.',
  'encounter.hivAdherence.includes.1': 'Adherence assessment',
  'encounter.hivAdherence.includes.2': 'Barrier identification',
  'encounter.hivAdherence.includes.3': 'Strategy development',
  'encounter.hivAdherence.includes.4': 'Reminder tools and resources',
  'encounter.hivAdherence.ideal': 'For patients who need support maintaining their medication schedule.',

  'encounter.hepC.title': 'Hepatitis C',
  'encounter.hepC.subtitle': 'Testing & Treatment',
  'encounter.hepC.description': 'Hepatitis C screening, treatment, and cure verification.',
  'encounter.hepC.overview': 'Comprehensive Hepatitis C testing, treatment coordination, and cure verification services.',
  'encounter.hepC.includes.1': 'Hepatitis C testing',
  'encounter.hepC.includes.2': 'Treatment coordination',
  'encounter.hepC.includes.3': 'Liver health monitoring',
  'encounter.hepC.includes.4': 'Cure verification',
  'encounter.hepC.ideal': 'For individuals needing Hepatitis C screening or treatment.',

  'encounter.wellness.title': 'Wellness',
  'encounter.wellness.subtitle': 'General Health',
  'encounter.wellness.description': 'General wellness check-up and health consultation.',
  'encounter.wellness.overview':
    'A general wellness appointment to discuss your overall health, address concerns, and develop strategies for maintaining optimal wellbeing.',
  'encounter.wellness.includes.1': 'General health review',
  'encounter.wellness.includes.2': 'Preventive care discussion',
  'encounter.wellness.includes.3': 'Health goal setting',
  'encounter.wellness.includes.4': 'Resource recommendations',
  'encounter.wellness.ideal': 'For individuals seeking general health guidance and wellness support.',

  'encounter.weightLoss.title': 'Weight Loss',
  'encounter.weightLoss.subtitle': 'Weight Management',
  'encounter.weightLoss.description': 'Medical weight loss consultation and support.',
  'encounter.weightLoss.overview':
    'Medical weight management consultation including evaluation, treatment options, and ongoing support for your weight loss journey.',
  'encounter.weightLoss.includes.1': 'Health evaluation',
  'encounter.weightLoss.includes.2': 'Treatment options discussion',
  'encounter.weightLoss.includes.3': 'Personalized plan development',
  'encounter.weightLoss.includes.4': 'Ongoing support',
  'encounter.weightLoss.ideal': 'For individuals seeking medical support for weight management.',

  // Prescription (Singular)
  'prescription.na': 'N/A',
  'prescription.pharmacy.phone': 'Phone: {phone}',
  'prescription.pharmacy.phone.a11y': 'Call pharmacy at {phone}',
  'prescription.pharmacy.name.a11y': 'Pharmacy name: {name}',
  'prescription.pharmacy.title': 'Pharmacy',
  'prescription.pharmacy.call': 'Call',
  'prescription.pharmacy.call.a11y': 'Call {name} at {phone}',
  'prescription.details.ariaLabel': 'Prescription details',
  'prescription.details.title': 'Prescription Details',
  'prescription.details.view': 'View Details',
  'prescription.provider.title': 'Prescribing Provider',
  'prescription.refills.deliveredOn': 'Delivered on: {date}',
  'prescription.refills.delivered': 'Delivered',
  'prescription.refills.fill': 'Fill {number}',
  'prescription.refills.pending': 'Pending',
  'prescription.refills.remainingLabel': 'Remaining refills',
  'prescription.refills.remainingValue': '{remain} of {total}',
  'prescription.refills.shipped': 'Shipped',
  'prescription.refills.shippedOn': 'Shipped on: {date}',
  'prescription.refills.status.delivered': 'Delivered',
  'prescription.refills.status.inTransit': 'In Transit',
  'prescription.refills.status.pending': 'Pending',
  'prescription.refills.title': 'Refill History',
  'prescription.refills.tracking': 'Tracking: {trackingNumber}',
  'prescription.refills.trackShipment': 'Track Shipment',
  'prescription.stats.askPharmacy': 'Your pharmacy can confirm how many refills are left',
  'prescription.stats.fillOf': 'Fill {current} of {total}',
  'prescription.stats.fillTotal': '1 fill total',
  'prescription.stats.fillsTotal': '{total} fills total',
  'prescription.stats.noRefillsLeft': 'No refills left',
  'prescription.stats.perFill': '{quantity} per fill',
  'prescription.stats.refillLeft': '1 refill left',
  'prescription.stats.refillsLeft': '{count} refills left',
  'prescription.submissionDate.title': 'Date Prescription Submitted',
  'prescription.title': 'Prescription',
  'prescription.trackShipment': 'Track Shipment',
  'prescription.unknownMedication': 'Unknown Medication',

  // Prescriptions (Plural)
  'prescriptions.card.a11y.label': 'Prescription for {drugName}, status: {status}, ordered: {date}',
  'prescriptions.empty.description':
    "After your provider visit and lab work are complete, they'll prescribe exactly what you need. You'll get a notification when it's ready!",
  'prescriptions.empty.title': 'Your medications are on the way!',
  'prescriptions.title': 'Prescriptions',

  // Lab Results (Plural)
  'labs.actions.beingReviewed': 'Being reviewed',
  'labs.actions.openOrder': 'Open Order',
  'labs.actions.openOrder.a11y': 'Open Quest order form',
  'labs.actions.orderNotAvailable': 'Order not available',
  'labs.actions.track': 'Track',
  'labs.tracking.outbound': 'On the way to you',
  'labs.tracking.inbound': 'On the way to the lab',
  'labs.order.title': 'Your lab order',
  'labs.order.detail': 'For your lab visit',
  'labs.instructions.title': 'Mailing it back',
  'labs.instructions.detail': 'Hand it in at a USPS Post Office',
  'labs.results.title': 'Your results',
  'labs.results.view.a11y': 'Open your lab results',
  'labs.actions.track.a11y': 'Track shipment',
  'labs.actions.view': 'View',
  'labs.actions.view.a11y': 'View lab details',
  'labs.defaultLabName': 'Lab',
  'labs.details.dateAdded': 'Date Added',
  'labs.details.dateOrdered': 'Date Ordered',
  'labs.details.dateReceived': 'Date Received',
  'labs.details.lab': 'Lab',
  'labs.details.dateReleased': 'Results Released',
  'labs.shipping.returnLabel': 'Return label',
  'labs.details.a11y.label': 'Lab Details',
  'labs.details.encounterReason': 'Encounter Reason',
  'labs.details.labType.a11y': 'Lab type: {labType}',
  'labs.manual.patientCboLabs': 'Patient/CBO Labs',
  'labs.details.mtl.pricingInfo':
    'The total price for your at-home MTL lab services is bundled for your convenience into one price. However, the total amount will be billed to your provider. If you would like the exact price paid for the service, please contact hello@qcareplus.com',
  'labs.details.panels': 'Panels',
  'labs.details.requisitionId': 'Requisition ID',
  'labs.details.status': 'Status',
  'labs.disclaimer.title': 'Disclaimer',
  'labs.disclaimer.manual':
    'These results are from a laboratory that is not affiliated with Q Care Plus. Typically these come from your community partner or could have been provided by your Primary Care Provider.',
  'labs.empty.description':
    "Once you meet with your provider, they'll order the perfect lab work for you. It could be a convenient at-home kit or a quick visit to a local lab!",
  'labs.empty.title': 'Your lab adventure awaits!',
  'labs.title': 'Labs',
  'labs.upload.description':
    'If you have lab work results from somewhere other than Q Care Plus, you can upload them below. Please be sure to include your name, date of birth and date of collection.',
  'labs.upload.alert.title': 'Please complete your profile',
  'labs.upload.alert.description': 'You must complete all of the required items in your profile before you can upload any lab work.',
  'labs.upload.successMessage': 'Lab document was uploaded',
  'labs.upload.ariaLabel': 'Upload your lab results',
  'labs.files.downloadOrder.a11y': 'Download order document {filename}',
  'labs.files.downloadResult.a11y': 'Download result file {filename}',
  'labs.files.orderDocument': 'Order Document',
  'labs.files.results': 'Results',
  'labs.sections.details': 'Details',
  'labs.sections.files': 'Files',
  'labs.sections.observations': 'Observations',
  'labs.sections.shipping': 'Shipping',
  'labs.shipping.address': 'Address',
  'labs.shipping.addressNotFound': 'Address not found',
  'labs.shipping.delivered': 'Delivered',
  'labs.shipping.shipped': 'Shipped',
  'labs.shipping.toLab': 'To Lab',
  'labs.shipping.toYou': 'To You',
  'labs.shipping.tracking': 'Tracking',
  'labs.stats.dateAdded': 'Date Added',
  'labs.stats.dateOrdered': 'Date Ordered',
  'labs.stats.dateReceived': 'Date Received',
  'labs.stats.dateShipped': 'Date Shipped',
  'labs.stats.delivered': 'Delivered',
  'labs.stats.positiveResults': 'Positive Results',
  'labs.stats.status': 'Status',
  'labs.stats.tracking': 'Tracking',
  'labs.stats.unknown': 'Unknown',
  'labs.status.pending': 'Pending',
  'labs.status.received': 'Received',
  'labs.notAvailable': 'Not Available',
  'labs.resultsNotFound': 'Results not found',
  'labs.types.ash': 'Ash Home Kit',
  'labs.types.ghmc': 'GMHC',
  'labs.types.manual': 'Patient/CBO lab work',
  'labs.types.mtl': 'MTL Home Kit',
  'labs.types.quest': 'Quest',

  // Lab Results (Singular)
  'lab.title': 'Lab',
  'lab.provider.title': 'Ordering Provider',

  // HIV Lab Results
  'hivLab.form.attestation.text':
    "I, {user}, confirm that I have completed this HIV self-test according to the manufacturer's instructions and that the information provided above is accurate.",
  'hivLab.form.collectionDate.label': 'Test Collection Date',
  'hivLab.form.collectionDate.placeholder': 'Select the date you took the test',
  'hivLab.form.description':
    'Please complete all fields below and upload clear photos of your test results. Ensure all information is accurate before submitting.',
  'hivLab.form.expirationDate.label': 'Test Kit Expiration Date',
  'hivLab.form.expirationDate.placeholder': 'Select the expiration date on your test kit',
  'hivLab.form.lotNumber.label': 'Test Kit Lot Number',
  'hivLab.form.lotNumber.placeholder': 'Enter the lot number from your test kit',
  'hivLab.form.resultPhotos.label': 'Upload Photos of Test Results',
  'hivLab.form.resultPhotos.placeholder': 'Take clear photos of your complete test results',
  'hivLab.form.submit.button': 'Submit Test Results',
  'hivLab.form.success.message':
    'Your HIV self-test results have been successfully submitted and will be reviewed by your healthcare provider.',
  'hivLab.form.success.title': 'Test Results Submitted',
  'hivLab.form.testResult.label': 'HIV Test Result',
  'hivLab.form.title': 'HIV Self-Test Results',
  'hivLab.form.validation.attestation.required': 'You must confirm the accuracy of your test results to submit',
  'hivLab.form.validation.collectionDate.afterExpiration': 'Test collection date cannot be after the kit expiration date',
  'hivLab.form.validation.collectionDate.future': 'Test collection date cannot be in the future',
  'hivLab.form.validation.resultPhotos.required': 'Please upload at least one clear photo of your test results',
  'hivLab.testResults.negative': 'Negative',
  'hivLab.testResults.notValid': 'Not Valid',
  'hivLab.testResults.positive': 'Positive',

  // Health Assessment (Singular)
  'assessment.dialog.completedOn': 'Completed {date}',
  'assessment.dialog.completedOn.aria': 'Assessment completed on {date}',
  'assessment.encounterId': 'Encounter ID: {id}',
  'assessment.responseInfo.answers': 'Answers ({count})',
  'assessment.responseInfo.status': 'Status:',
  'assessment.responseInfo.title': 'Response Information',
  'assessment.responseStatus.noResponse': 'No Response',
  'assessment.responseStatus.received': 'Received',
  'assessment.responseStatus.receivedReviewed': 'Received (Reviewed)',
  'assessment.responseStatus.sentToPatient': 'Sent to patient',
  'assessment.status.complete': 'Complete',
  'assessment.status.expired': 'Expired',
  'assessment.status.finished': 'Finished',
  'assessment.status.in_progress': 'In Progress',
  'assessment.status.pending': 'Pending',
  'assessment.status.reviewed': 'Reviewed',
  'assessment.title': 'Assessment',

  // Health Assessments (Plural)
  'assessments.async.canceled.returnButton': 'Return to Dashboard',
  'assessments.async.canceled.title': '{name} Canceled',
  'assessments.async.completed.returnButton': 'Return to Async Appointment',
  'assessments.async.completed.title': '{name} Completed',
  'assessments.async.enterAnswer': 'Enter your answer...',
  'assessments.async.enterNumber': 'Enter a number...',
  'assessments.async.enterPercentage': 'Enter percentage',
  'assessments.async.exactPercentage': 'Exact percentage',
  'assessments.async.importantInformation': 'Important Information',
  'assessments.async.notSet': 'Not set',
  'assessments.async.percentage': 'Percentage',
  'assessments.async.progress': 'Progress',
  'assessments.async.selectDate': 'Select a date',
  'assessments.async.unsupportedQuestionType': 'Unsupported question type',
  'assessments.expired.description': 'This assessment has expired. Please contact your provider if you need assistance.',
  'assessments.expired.title': '{name} Expired',
  'assessments.reviewed.description': 'Your provider has reviewed this assessment. Thank you for your responses.',
  'assessments.reviewed.title': '{name} Reviewed',
  'assessments.completed.description': 'Thank you for completing this assessment. Your responses have been submitted successfully.',
  'assessments.completed.title': '{name} Completed',
  'assessments.default.name': 'Health Assessment',
  'assessments.default.description':
    'This questionnaire is to check on how things are going with your treatment. Your responses will be sent to your provider at QCare+.',
  'assessments.quarterly_lab.name': 'Quarterly Renewal Health Assessment',
  'assessments.quarterly_lab.description': 'Please answer the following questions for your PrEP quarterly review.',
  'assessments.sti.name': 'STI Health Assessment',
  'assessments.sti.description':
    'Your provider requested you receive treatment for an STI at a clinic in your area or local department of health. Please complete this form sharing the date when you received your treatment, the clinic name & address, and the medication that you received.',
  'assessments.pep.name': 'PEP Health Assessment',
  'assessments.pep.description':
    'Please answer all questions prior to submitting. Your responses will be reviewed by your provider. In the interim, please also continue your medication as prescribed and complete the lab testing.',
  'assessments.hiv.name': 'HIV Health Assessment',
  'assessments.hiv.description':
    'This questionnaire is to check on how things are going with your treatment. Your responses will be sent to your provider at QCare+.',
  'assessments.prep_post_encounter.name': 'PrEP Initial Post Encounter Med Assessment',
  'assessments.prep_post_encounter.description':
    'This questionnaire is to check on how things are going with your treatment. Your responses will be sent to your provider at QCare+.',
  'assessments.prep.initial.name': 'PrEP Assessment',
  'assessments.prep.initial.description':
    'Complete your PrEP health assessment to help our providers understand your needs and determine your eligibility.',
  'assessments.sti.testing.name': 'STI Testing Assessment',
  'assessments.sti.testing.description':
    'Complete your STI testing health assessment to help our providers understand your needs and determine your eligibility.',
  'assessments.card.a11y.hint': 'Tap to view assessment details',
  'assessments.card.a11y.label': 'Assessment {name}, status: {status}, created: {date}',
  'assessments.empty.description':
    "We'll occasionally send you quick health assessments to make sure you're getting the best care possible. Keep an eye out for our messages!",
  'assessments.empty.title': 'Ready for your health check-ins!',
  'assessments.title': 'Assessments',

  // Enrollment Process
  'enrollment.cboStep.agreeSharing': 'I consent to share my information with the selected community partner to support my telehealth care.',
  'enrollment.cboStep.assignmentUpdate.outsideCoverageInsured':
    "Your home address has been updated to a location outside your community partner's coverage area. We're unable to continue to support your PrEP care through your current community partner. Contact Customer Support for help.",
  'enrollment.cboStep.assignmentUpdate.outsideCoverageUninsured':
    "Your home address has been updated to a location outside your community partner's coverage area. Because you're uninsured, we're unable to continue to support your PrEP care through your current community partner. For insurance resources, you may want to explore options such as {prideHealth} or {americanExchange}. Contact Customer Support for help.",
  'enrollment.cboStep.assignmentUpdate.uninsured':
    "You've updated your insurance status to uninsured and we're unable to continue to support your PrEP care. Please message customer support for resources.",
  'enrollment.cboStep.assignmentUpdateTitle': 'Community Based Organization Update',
  'enrollment.cboStep.cashPayNotAvailable': 'We are currently not offering self pay in your state',
  'enrollment.cboStep.cashPayNotAvailableDescription':
    'We are frequently updating the states that we service and will notify you when your state becomes available.',
  'enrollment.cboStep.description':
    'These are local organizations near you. In most cases they help cover the full cost of your visits and prescriptions.',
  'enrollment.cboStep.greatTitle': "Great, you've been added",
  'enrollment.cboStep.noPartnerDescription':
    'We need to work with a local partner to provide care.\nWe add new partners frequently, you will be notified when one is available.',
  'enrollment.cboStep.noPartnerTitle': 'Looks like none of our partners are currently available',
  'enrollment.cboStep.noServices': 'No services listed',
  'enrollment.cboStep.notSelectedState': 'Not selected',
  'enrollment.cboStep.partnersButton': 'Learn about our community partners',
  'enrollment.cboStep.partnersDialogTitle': 'Community Partners',
  'enrollment.cboStep.partnersInfo1':
    'We work together with local community partners that help find programs to lower the cost of care to patients.',
  'enrollment.cboStep.partnersInfo2': 'In most cases they help cover the full cost of visits and prescriptions.',
  'enrollment.cboStep.partnersInfo3': 'And they work with our Medical Providers to ensure you have the best experience.',
  'enrollment.cboStep.resetInfo': 'If any of your information has changed, please consider to start the enrollment flow again.',
  'enrollment.cboStep.selectedState': 'Selected',
  'enrollment.cboStep.servicesOffered': 'Services offered: {services}',
  'enrollment.cboStep.sorryDescription':
    'You can always come back to Q Care Plus at a later date and check your enrollment.\n\nWe add new partners and benefit programs frequently.',
  'enrollment.cboStep.sorryTitle': 'Sorry to see you go',
  'enrollment.cboStep.startOver': 'Start over',
  'enrollment.cboStep.subscribe': 'Ok!',
  'enrollment.cboStep.title': 'Choose your community partner',
  'enrollment.cboStep.waitlistConfirmation': 'As soon as we have available spots, our team will contact you directly.',
  'enrollment.consent.agreeTerms': 'You must agree to the terms and conditions',
  'enrollment.consent.privacyPolicy': 'Privacy Policy',
  'enrollment.consent.telehealthConsent': 'Telehealth Consent',
  'enrollment.consent.termsOfService': 'Terms of Service',
  'enrollment.consent.text': 'I have read and agree to the {termsOfService}, {privacyPolicy}, and {telehealthConsent}.',
  'enrollment.consentStep.description':
    'Last thing before your visit – please confirm your consent to be treated by our expert provider team. This includes your virtual consultation as well as lab work and medications if prescribed.',
  'enrollment.consentStep.done': 'Done',
  'enrollment.consentStep.form.error.notMatching': 'Must be initials of first and last name',
  'enrollment.consentStep.instructions': 'Signing as {name}. Enter your initials below.',
  'enrollment.consentStep.title': 'One last signature',
  'enrollment.continueInApp.accept': 'Open',
  'enrollment.continueInApp.decline': 'Not now',
  'enrollment.continueInApp.description': 'Free on iOS and Android',
  'enrollment.continueInApp.opening': 'Opening…',
  'enrollment.continueInApp.title': 'Sign up faster in the app',
  'enrollment.countyStep.title': 'Which county do you live in?',
  'enrollment.countyStep.label': 'County',
  'enrollment.countyStep.description': 'We match you with a community partner near you, so we need the county.',
  'enrollment.detailsStep.address.city': 'City',
  'enrollment.detailsStep.address.cityPlaceholder': 'Enter city',
  'enrollment.detailsStep.address.homeDescription':
    "Confirms we're licensed in your state. Labs and prescriptions arrive in discreet packaging.",
  'enrollment.detailsStep.address.shippingDescription': 'Send labs and prescriptions somewhere other than home.',
  'enrollment.detailsStep.address.homeTitle': 'Where do you live?',
  'enrollment.detailsStep.address.line1': 'Address',
  'enrollment.detailsStep.address.line2': 'Apt. Suite',
  'enrollment.detailsStep.address.line2Placeholder': 'Apt, Suite, Unit, etc.',
  'enrollment.detailsStep.address.shippingTitle': 'Where should we send things?',
  'enrollment.detailsStep.address.state': 'State',
  'enrollment.detailsStep.address.stateSearchPlaceholder': 'Search states...',
  'enrollment.detailsStep.address.useHomeAsShipping': "I'd like to use this as my shipping address for labs/medications as well.",
  'enrollment.detailsStep.address.zip': 'Zipcode',
  'enrollment.detailsStep.address.zipPlaceholder': 'Enter 5-digit ZIP code',
  'enrollment.detailsStep.birthdate.description': 'You need to be at least 18.',
  'enrollment.detailsStep.payoff.title': 'Your account is ready',
  'enrollment.detailsStep.payoff.description': 'These details go on your chart, so we can book your appointment and ship your medication.',
  'enrollment.detailsStep.nextUp': "Up next: a couple of quick questions about your area and coverage, then you're done",
  'enrollment.detailsStep.birthdate.title': 'When were you born?',
  'enrollment.detailsStep.contactInfo.receiveNotifications':
    'Text me refill and appointment reminders. Message frequency varies, data rates may apply.',
  'enrollment.detailsStep.contactInfo.title': "What's your phone number?",
  'enrollment.detailsStep.contactInfo.description': 'Optional. We only text about refills and appointments.',
  'enrollment.detailsStep.errors.nyResidencyRestriction':
    'New York residents must use a New York shipping address for lab work and prescriptions',
  'enrollment.detailsStep.errors.phoneInUse': 'Phone is already in use',
  'enrollment.detailsStep.errors.stateDoesntBelong': 'State must belong to entered ZIP Code',
  'enrollment.detailsStep.errors.zipInvalid': 'Please enter valid 5-digit ZIP code',
  'enrollment.detailsStep.optOutDialog.description':
    'We will only send you messages related to your care, upcoming appointments, deliveries, and notifications of documents for your review.',
  'enrollment.detailsStep.optOutDialog.title': 'To provide the best experience we recommend opting in to text messages',
  'enrollment.detailsStep.personalInfo.description': 'Exactly as it appears on your government issued ID.',
  'enrollment.detailsStep.personalInfo.firstName': 'First name',
  'enrollment.detailsStep.personalInfo.firstNamePlaceholder': 'Enter your first name',
  'enrollment.detailsStep.personalInfo.lastName': 'Last name',
  'enrollment.detailsStep.personalInfo.lastNamePlaceholder': 'Enter your last name',
  'enrollment.detailsStep.personalInfo.title': "What's your legal name?",
  'enrollment.detailsStep.timezone.description': 'So we can show you available appointment times.',
  'enrollment.detailsStep.timezone.label': 'Timezone',
  'enrollment.detailsStep.timezone.placeholder': 'Select timezone',
  'enrollment.detailsStep.timezone.title': 'What time zone are you in?',
  'enrollment.detailsStep.securityAlert.title': 'Your information is encrypted, and never shared without your say-so',
  'enrollment.emailStep.contactSupport': 'Failed to create account. Please contact support.',
  'enrollment.emailStep.description1': 'Just answer a few short questions to start or schedule your online consultation.',
  'enrollment.emailStep.description2': 'No payment required',
  'enrollment.emailStep.email.label': 'Email',
  'enrollment.emailStep.email.placeholder': 'Enter your email',
  'enrollment.emailStep.title': "Let's start with your email",
  'enrollment.emailStep.unavailable': 'Email unavailable. Please enter a new email address.',
  'enrollment.insuranceStep.dialog.content1':
    'Our team can help by enrolling you into Gilead Advancing Access® or Co-Pay Program, which will likely help cover the cost of your medication.',
  'enrollment.insuranceStep.dialog.content2':
    'Please note, in order to qualify for PrEP at no cost, you must not earn more than $62,450 annually. This information is confidential and is only shared with the drug maker Gilead and no credit checks are performed.',
  'enrollment.insuranceStep.dialog.content3':
    'If you make over the amount, we have a few other solutions we can enroll you into to ensure your medications are affordable.',
  'enrollment.insuranceStep.dialog.title': 'Gilead Advancing Access® and Co-Pay Program',
  'enrollment.insuranceStep.error.gileadConsent': 'Must accept to continue',
  'enrollment.insuranceStep.error.invalidHousehold': 'Please enter a valid Household size',
  'enrollment.insuranceStep.error.invalidIncome': 'Please enter a valid Income',
  'enrollment.insuranceStep.form.annualIncome': 'Annual household income, before taxes',
  'enrollment.insuranceStep.form.annualIncome.helper':
    'Only used to check whether Gilead covers your medication. It never affects your care.',
  'enrollment.insuranceStep.form.payoff.title': "You're through the eligibility questions",
  'enrollment.insuranceStep.form.payoff.description': 'This is the last set, and it is what unlocks free medication.',
  'enrollment.insuranceStep.form.description':
    "If you don't have insurance or have been denied coverage by your plan, we can help.\n\nWe work with the Gilead Advancing Access® or Co-Pay Program.\n\nWe require some additional information to help our team do all the work for you!",
  'enrollment.insuranceStep.form.gileadConsent': "I consent to sharing my health data with Gilead's Patient Assistance Program",
  'enrollment.insuranceStep.form.householdSize': 'Household size',
  'enrollment.insuranceStep.form.learnMore': 'Learn more',
  'enrollment.insuranceStep.form.medicaidEligible': 'Eligible for Medicaid',
  'enrollment.insuranceStep.form.selectPlaceholder': 'Select',
  'enrollment.insuranceStep.form.title': 'We can help with the cost',
  'enrollment.insuranceStep.form.veteranBenefits': 'Veteran Affairs Benefits',
  'enrollment.insuranceStep.question.no': 'I do not',
  'enrollment.insuranceStep.question.title': 'Do you have health insurance?',
  'enrollment.insuranceStep.question.description': 'This decides which cost assistance we check for you.',
  'enrollment.insuranceStep.question.yes': 'I do',
  'enrollment.insuranceStep.waitingList.description':
    'Our partners frequently add programs to help uninsured patients receive care.\n\nWould you like to be notified when these are available?',
  'enrollment.insuranceStep.waitingList.no': 'No thanks',
  'enrollment.insuranceStep.waitingList.title': "We're sorry but {cboName} requires personal insurance",
  'enrollment.insuranceStep.waitingList.yes': 'Yes!',
  'enrollment.medicareStep.checkHere': 'check here',
  'enrollment.medicareStep.helpText':
    'Medi‑CAL is California\'s version of Medicaid. It\'s public health insurance (not to be confused with the word "medical").',
  'enrollment.medicareStep.no': 'I do not',
  'enrollment.medicareStep.title': 'Do you have Medi-CAL?',
  'enrollment.medicareStep.unsureText': 'If you are unsure, you can',
  'enrollment.medicareStep.yes': 'I do',
  'enrollment.navigation.back': 'Back',
  'enrollment.navigation.backTo': 'Back to {appName}',
  'enrollment.navigation.backHint': 'Go back to previous step from {stepName}',
  'enrollment.navigation.loading': 'Loading, please wait',
  'enrollment.navigation.next': 'Next Step',
  'enrollment.navigation.nextHint': 'Continue to next step from {stepName}',
  'enrollment.screen.a11y.label': 'Account enrollment form',
  'enrollment.progress.a11y.label': 'Enrollment progress, {phase}',
  'enrollment.selectFlow.a11y.benefit': 'Benefit',
  'enrollment.selectFlow.a11y.hint': 'Press to select this enrollment option',
  'enrollment.selectFlow.insurance.description':
    "Most insured patients pay $0 for visits, labs, and prescriptions. That's why we don't ask for a payment method!",
  'enrollment.selectFlow.insurance.title': 'With insurance',
  'enrollment.selectFlow.noInsurance.benefit.pricing': 'Transparent, up-front pricing',
  'enrollment.selectFlow.noInsurance.description':
    'No insurance, no problem! We offer convenient cash pay options for the quality care you need and with zero surprises.',
  'enrollment.selectFlow.noInsurance.title': 'Without insurance',
  'enrollment.selectFlow.title': 'Connect to a provider in minutes by video or via messaging*',
  'enrollment.selectFlow.disclaimer': '*messaging option is based on state regulations and may not be available in every state',
  'enrollment.selectFlow.commonBenefits.title': 'Included with both options',
  'enrollment.selectFlow.benefit.delivery': '100% online services and doorstep delivery',
  'enrollment.selectFlow.benefit.appointments': 'Same-and next-day care available',
  'enrollment.selectFlow.benefit.hipaa': 'Confidential and HIPAA-compliant',
  'enrollment.selectFlow.benefit.providers': 'Licensed providers',
  'enrollment.steps.consent': 'Consent',
  'enrollment.steps.insurance': 'Insurance & Consent',
  'enrollment.steps.location': 'Location & Eligibility',
  'enrollment.steps.personal': 'Personal Information',
  'enrollment.steps.personal.short': 'Personal',
  'enrollment.title': 'Tell us about yourself!',
  'enrollment.toast.continueEnrollment':
    'Your enrollment process needs a little more attention. Please continue to next steps to complete it',

  // Appointments (Singular)
  'appointment.actions.a11y.group': 'Appointment actions',
  'appointment.actions.cancel': 'Cancel',
  'appointment.actions.cancel.aria': 'Cancel appointment',
  'appointment.actions.cancel.hint': 'Press to cancel this appointment',
  'appointment.actions.join': 'Join',
  'appointment.actions.join.accessibility': 'Join video appointment',
  'appointment.actions.join.hint': 'Press to join the video call',
  'appointment.actions.message': 'Message',
  'appointment.actions.message.accessibility': 'Message provider',
  'appointment.actions.message.hint': 'Press to send a message to your provider',
  'appointment.actions.message.provider.accessibility': 'Message {name}',
  'appointment.actions.reschedule': 'Reschedule',
  'appointment.actions.reschedule.accessibility': 'Reschedule appointment',
  'appointment.actions.reschedule.hint': 'Press to change appointment time',
  'appointment.actions.viewSummary': 'View After Visit Summary',
  'appointment.actions.viewSummary.aria': 'View appointment visit summary',
  'appointment.actions.viewSummary.hint': 'Press to open the visit summary document',
  'appointment.detail.cancelAppointment': 'Cancel Appointment',
  'appointment.detail.messageProvider': 'If you have any questions, please send a message to your provider.',
  'appointment.detail.messageProvider.aria': 'Instructions to message your provider with questions',
  'appointment.async.cancel.a11y.hint': 'Press to cancel this appointment',
  'appointment.async.cancel.a11y.label': 'Cancel async messaging appointment',
  'appointment.async.cancel.confirm': 'Yes, Cancel',
  'appointment.async.cancel.message': 'Are you sure you want to cancel your async visit request?',
  'appointment.async.cancel.title': 'Cancel appointment?',
  'appointment.async.complete.message': 'Your async visit is complete. Press to view visit summary.',
  'appointment.async.description':
    'Submit your information in our encrypted platform. A licensed provider will review and follow up during business hours.',
  'appointment.async.establish.description':
    'Submit your information in our encrypted platform. A licensed provider will review and follow up with you during your scheduled appointment.',
  'appointment.async.establish.expectations.questionnaire': 'Complete an online, confidential health questionnaire.',
  'appointment.async.establish.expectations.appointment':
    "A quick 5 minute video or phone appointment is needed based on your state's regulations.",
  'appointment.async.establish.expectations.response':
    "If eligible, you'll receive prescription(s) confirmation or treatment recommendations during this appointment.",
  'appointment.async.establish.hivInitial.expectations.questionnaire':
    'Complete an online, confidential health questionnaire to determine your program eligibility',
  'appointment.async.establish.hivInitial.expectations.appointment': 'A video appointment is needed',
  'appointment.async.documenting.message': 'Your request is under provider review',
  'appointment.async.error.existing': 'You already have Asynchronous Visit booked',
  'appointment.async.error.unavailable': 'We apologize, but there are no available providers right now',
  'appointment.async.expectations.clarifications': 'Your assigned provider may send in-app messages for clarification if needed',
  'appointment.async.expectations.questionnaire': 'Complete an online, confidential health questionnaire. No live visit required',
  'appointment.async.expectations.response':
    "If eligible, you'll receive prescription(s) confirmation or treatment recommendations within 1-2 business days (usually within hours!).",
  'appointment.async.expectations.title': 'How this Works:',
  'appointment.async.incomplete.message':
    'Finish submitting your information so our licensed provider can review and follow-up with you during business hours.',
  'appointment.async.resume': 'Resume',
  'appointment.async.resume.a11y.hint': 'Press to continue with this appointment',
  'appointment.async.resume.a11y.label': 'Resume async messaging appointment',
  'appointment.async.status.complete': 'Complete',
  'appointment.async.status.incomplete': 'Incomplete',
  'appointment.async.status.underReview': 'Under Review',
  'appointment.async.submit': 'Get Started Online Now',
  'appointment.async.title': 'Start Online Questionnaire',
  'appointment.async.visitType': 'Async Visit',
  'appointment.async.waiting.message': "Your request is being reviewed by our providers. You'll be notified when a provider responds.",
  'appointment.booking.accessibility.book': 'Book an Appointment',
  'appointment.booking.confirm.button.aria': 'Confirm appointment booking',
  'appointment.booking.confirm.button.hint': 'Press to confirm and book your appointment',
  'appointment.booking.confirm.description': 'Review your appointment details and complete the required information below',
  'appointment.booking.confirm.screen.aria': 'Appointment confirmation screen',
  'appointment.booking.confirm.title': 'Confirm Your Appointment',
  'appointment.booking.error.alreadyScheduled': 'You already have an appointment scheduled',
  'appointment.booking.error.cannotSelfSchedule': 'This appointment could not be scheduled. Please try again.',
  'appointment.booking.error.general.message':
    "We are really sorry but we couldn't schedule that appointment.\n\nPlease try again or contact our customer support.",
  'appointment.booking.error.time.unavailable.message': 'Sorry, but that time is no longer available. Please select a new time.',
  'appointment.booking.error.time.unavailable.title': 'Time Unavailable',
  'appointment.booking.provider.notFound.title': 'Provider not found',
  'appointment.booking.provider.notFound.description':
    'The selected provider could not be found. Please go back and select a different provider.',
  'appointment.booking.success.modified': 'Your appointment has been modified as requested.',
  'appointment.booking.success.scheduled': 'Your appointment has been scheduled as requested.',
  'appointment.booking.success.title': 'All Set!',
  'appointment.booking.type.choose': 'Choose Appointment Type',
  'appointment.booking.type.phone.aria': 'Phone appointment option',
  'appointment.booking.type.phone.hint': 'Select phone appointment type',
  'appointment.booking.type.phone.subtitle': 'Voice only appointment',
  'appointment.booking.type.phone.title': 'Phone Call',
  'appointment.booking.type.video.aria': 'Video appointment option',
  'appointment.booking.type.video.hint': 'Select video appointment type',
  'appointment.booking.type.video.subtitle': 'Virtual visit with video',
  'appointment.booking.type.video.title': 'Video Call',
  'appointment.booking.update.confirm.aria': 'Confirm appointment update',
  'appointment.booking.update.title': 'Update Appointment',
  'appointment.calendar.no.timeslots': 'No available time slots',
  'appointment.calendar.date.available.aria': '{date}, {day}, available time slots',
  'appointment.calendar.date.unavailable.aria': '{date}, no available time slots',
  'appointment.calendar.selectDate.hint': 'Press to select this date',
  'appointment.calendar.today': 'Today',
  'appointment.calendar.tomorrow': 'Tomorrow',
  'appointment.calendar.unavailable': 'Unavailable',
  'appointment.cancel.dialog.message': 'Would you like to cancel your existing appointment?',
  'appointment.cancel.dialog.title': 'Cancel appointment?',
  'appointment.canceled.message': 'Your appointment was cancelled.',
  'appointment.card.a11y.label': '{visitReason} appointment - Status: {status} - Date: {date}',
  'appointment.complete.message': 'The appointment was completed.',
  'appointment.completed.title': 'Appointment Completed',
  'appointment.dateTime.a11y.label': 'Appointment scheduled for {day}, {date} at {time}',
  'appointment.detail.screen.accessibility': '{status} appointment with {provider}',
  'appointment.documenting.message': 'Your provider is currently updating your medical record.',
  'appointment.failed.message': 'Your appointment was not completed. This may have been due to a technical issue.',
  'appointment.failed.title': 'Appointment Failed',
  'appointment.failureMessage': 'This appointment was unable to be completed',
  'appointment.missed.message': 'Your appointment was not completed. Either you or your provider were not available at the scheduled time.',
  'appointment.missed.title': 'Appointment Missed',
  'appointment.notCompleted.message': 'This appointment was unable to be completed.',
  'appointment.unavailable.message': 'This appointment is no longer available.',
  'appointment.unavailable.title': 'Appointment Unavailable',
  'appointment.insurance.checking.title': 'Checking your insurance...',
  'appointment.insurance.checking.subtitle': 'This only takes a moment. Please stay on this screen.',
  'appointment.insurance.required.continue': 'Continue',
  'appointment.insurance.required.description':
    'We could not verify an active insurance plan. Add your insurance card to continue with your visit.',
  'appointment.insurance.required.title': 'Add your insurance',
  'appointment.onDemand.button.start': 'Start Immediate Visit',
  'appointment.onDemand.button.switch': 'Switch to Immediate Visit',
  'appointment.onDemand.error.abandoned.message':
    'We see that you have stepped away. Not a problem! Please schedule a new appointment at your earliest convenience.',
  'appointment.onDemand.error.abandoned.title': 'Session Ended',
  'appointment.onDemand.error.noProviders': 'No providers are available for on-demand appointments at this time',
  'appointment.onDemand.error.rejected.message':
    "We sincerely apologize for the delay due to our providers assisting other patients longer than anticipated. Let's go ahead and schedule a future appointment as soon as possible!",
  'appointment.onDemand.error.rejected.title': 'Appointment Delayed',
  'appointment.onDemand.features.connectImmediately':
    'Connect immediately with an available provider. A provider will typically join you within 1-2 minutes',
  'appointment.onDemand.features.establishCare': 'A brief 5-minute video visit to establish your care',
  'appointment.onDemand.features.secureConfidential': 'Same secure, confidential care as scheduled visits. Sessions take 15-20 minutes',
  'appointment.onDemand.features.urgentNeeds': 'Perfect for urgent but non-emergency health needs',
  'appointment.onDemand.loading.subtitle': 'Preparing your healthcare experience...',
  'appointment.onDemand.loading.title': 'Loading On-Demand Care',
  'appointment.onDemand.requiredInfo.step.tips': 'Tips',
  'appointment.onDemand.requiredInfo.photoId.description':
    'Please upload your ID, ensuring your name, date of birth, and picture are visible. If you’re unable to upload it now, please have it readily available for the provider to screenshot it at the beginning of your appointment.',
  'appointment.onDemand.requiredInfo.title': 'While you wait',
  'appointment.onDemand.screen.description': 'Connect with a provider right away - fast, easy, and convenient!',
  'appointment.onDemand.screen.title.book': 'Book Immediate Visit',
  'appointment.onDemand.screen.title.update': 'Switch to Immediate Visit',
  'appointment.onDemand.webview.a11y': 'On-demand appointment web interface',
  'appointment.onDemand.whatToExpect.title': 'What to Expect',
  'appointment.onDemand.waitingRoom.message.start':
    'We are working to connect you with an available provider. Please note this can take a few moments. Thank you for being here!',
  'appointment.onDemand.waitingRoom.message.3min':
    'Our team is working hard to connect you with the provider as quickly as possible. We appreciate you waiting — your care is our priority.',
  'appointment.onDemand.waitingRoom.message.5min':
    "Almost there! Your provider is just finishing up with another patient. Thank you for your continued patience — we'll be with you shortly!",
  'appointment.onDemand.waitingRoom.message.6min':
    "This is taking a little longer than we'd hoped, but the provider should be with you in just a moment. Thanks for hanging in there with us!",
  'appointment.onDemand.waitingRoom.message.9min': "You are important to us! We'll connect your provider as soon as we can.",
  'appointment.onDemand.waitingRoom.timer.aria': 'Elapsed time: {time}',
  'appointment.onDemand.waitingRoom.playGame': 'Play a game while you wait ;)',
  'appointment.onDemand.waitingRoom.success.accepted': 'Good news! A provider will be starting your visit momentarily.',
  'appointment.onDemand.waitingRoom.status': 'Connecting you with a provider',
  'appointment.payment.amount.subtitle': 'Payment only processed when you arrive',
  'appointment.payment.amount.title': 'Just {amount} to Reserve',
  'appointment.payment.button.processing': 'Securing Your Spot...',
  'appointment.payment.button.reserve': 'Reserve My Appointment for {amount}',
  'appointment.payment.description':
    'Lock in your preferred appointment time with our secure hold system. Zero charge until your visit begins!',
  'appointment.payment.error.failed': 'Payment failed. Please try again.',
  'appointment.payment.error.init': 'Unable to prepare payment method. Please try again or contact support if the issue persists.',
  'appointment.payment.error.processing': 'Payment processing encountered an unexpected issue. Please try again or contact support.',
  'appointment.payment.error.setup': 'Payment setup encountered an issue. Please check your connection and try again.',
  'appointment.payment.error.timeout': 'Payment setup is taking longer than expected. Please check your connection and try again.',
  'appointment.payment.features.cancel': 'Change your mind? Cancel anytime with zero fees',
  'appointment.payment.features.encryption': 'Military-grade encryption protects your information',
  'appointment.payment.features.hold': 'Your money stays in your account until appointment begins',
  'appointment.payment.features.secure': 'Instantly secure your preferred appointment time',
  'appointment.payment.features.title': 'Why Patients Love Our System',
  'appointment.payment.form.description': 'Add your payment method to instantly secure your appointment slot',
  'appointment.payment.form.title': 'Complete Your Reservation',
  'appointment.payment.success': 'Payment successful!',
  'appointment.payment.title': 'Reserve Your Spot Today',

  'appointment.provider.title': 'Healthcare Provider',
  'appointment.providerDetails.availableTimes': 'Available Times for {date}',
  'appointment.providerDetails.bio.readMore': 'Read bio',
  'appointment.providerDetails.bio.showLess': 'Show less',
  'appointment.providerDetails.noTimeslots.description': 'Please select a different date to see available appointment times.',
  'appointment.providerDetails.noTimeslots.title': 'No appointments available',
  'appointment.providerDetails.selectDate': 'Select a Date',
  'appointment.providerDetails.title': 'Select Date and Time',
  'appointment.schedule.afternoon': 'Afternoon',
  'appointment.schedule.dateSelector.aria': 'Date selector',
  'appointment.schedule.evening': 'Evening',
  'appointment.schedule.morning': 'Morning',
  'appointment.schedule.provider.calendar.aria': 'Provider availability calendar',
  'appointment.schedule.provider.select.hint': "Press to view this provider's details",
  'appointment.schedule.providers.description':
    'Select from our qualified healthcare providers who are available for your appointment. Each provider shows their earliest available times.',
  'appointment.schedule.providers.empty.description':
    'We are very sorry but there are no providers available at this time. Please proceed to your local clinic or emergency room for further assistance.',
  'appointment.schedule.providers.empty.title': 'No Available Providers',
  'appointment.schedule.providers.title': 'Providers',
  'appointment.schedule.timeSlot.select.hint': 'Press to select this time slot',
  'appointment.status.canceled': 'Canceled',
  'appointment.status.complete': 'Complete',
  'appointment.status.inProgress': 'In Progress',
  'appointment.status.missed': 'Missed',
  'appointment.status.notCompleted': 'Not Completed',
  'appointment.status.scheduled': 'Scheduled',
  'appointment.status.a11y.isScheduled': 'Appointment is scheduled',
  'appointment.status.a11y.status': 'Appointment status: {status}',
  'appointment.title': 'Appointment',
  'appointment.type.title': 'Visit Type',
  'appointment.type.async.establish.any.subtitle': 'State regulations require a 5 minute video or phone visit to establish care',
  'appointment.type.async.establish.subtitle': 'State regulations require a 5 minute video visit to establish care',
  'appointment.type.async.subtitle': 'No video visit required',
  'appointment.type.async.title': 'Start with an Online Form',
  'appointment.type.book.title': 'Choose Your Visit Type',
  'appointment.type.description': "Choose the option you're most comfortable with",
  'appointment.type.empty.description':
    'No appointment options are currently available for this service. Please contact support for assistance.',
  'appointment.type.empty.title': 'No Options Available',
  'appointment.type.emergency.call911.aria': 'Call 911 for medical emergencies',
  'appointment.type.emergency.subtitle': 'Call 911 immediately',
  'appointment.type.emergency.title': 'Medical Emergency',
  'appointment.type.ondemand.subtitle': 'See a provider immediately',
  'appointment.type.ondemand.title': 'Start an Online Visit Now',
  'appointment.type.reschedule.title': 'Reschedule Your Appointment',
  'appointment.type.schedule.subtitle': 'Book a Later Time',
  'appointment.type.schedule.title': 'Schedule a Future Visit',
  'appointment.treatment.title': 'Healthcare Service',
  'appointment.treatment.gotIt': 'Got It',
  'appointment.treatment.info.aria': 'More information',
  'appointment.treatment.select.description': "Choose the healthcare service you'd like to schedule an appointment for.",
  'appointment.treatment.select.title': 'Select Your Care',
  'appointment.treatment.whatsIncluded': "What's Included:",
  'appointment.video.instructions': 'We will send you email/text reminders with instructions and a link to attend your appointment.',
  'appointment.webview.aria': 'Appointment video consultation interface',
  'appointment.webview.loading': 'Preparing your healthcare experience...',

  // Appointments (Plural)
  'appointments.empty.description':
    'Schedule your first appointment and all your future visits will appear here. Your wellness adventure begins with just one click!',
  'appointments.empty.title': 'Your health journey starts here!',
  'appointments.list.a11y.label': 'Appointments list with {count} items',
  'appointments.title': 'Appointments',

  // Home Dashboard
  'home.badge.a11y.noTasks': 'No pending tasks',
  'home.badge.a11y.tasks': '{count} pending tasks',
  'home.empty.description': "You don't have any tasks at the moment.",
  'home.contact.careTeam.name': 'Your care team',
  'home.contact.careTeam.action': 'Message',
  'home.block.visits': 'Visits',
  'home.block.seeAll': 'See all',
  'home.block.labs': 'Labs',
  'labs.kit.step.sent': 'Sent',
  'labs.kit.step.you': 'You',
  'labs.kit.step.lab': 'Lab',
  'labs.kit.step.testing': 'Testing',
  'labs.kit.step.results': 'Results',
  'labs.kit.days': '{count, plural, one {# day} other {# days}}',
  'labs.kit.instructions.cta': 'See instructions',
  'labs.kit.support.detail': 'Kit questions and replacements',
  'labs.kit.stage.arrived.title': 'Your kit arrived',
  'labs.kit.stage.arrived.body': 'Collect your sample whenever you are ready.',
  'labs.kit.stage.collect.title': 'Collect your sample',
  'labs.kit.stage.collect.body': 'Delivered {date}. It takes about ten minutes.',
  'labs.kit.stage.mail.title': 'Mail your kit back',
  'labs.kit.stage.mail.body': 'Delivered {date}. We are still waiting on it.',
  'labs.kit.stage.overdue.title': 'Your kit is still waiting',
  'labs.kit.stage.overdue.body': 'Delivered {date}. Your results cannot start until the lab has it.',
  'labs.kit.progress.a11y.sent': 'Your kit is on the way to you',
  'labs.kit.progress.a11y.you': 'Your kit is with you and needs to be mailed back',
  'labs.kit.progress.a11y.lab': 'Your kit is on the way to the lab',
  'labs.kit.progress.a11y.testing': 'The lab is testing your sample',
  'labs.kit.progress.a11y.results': 'Your results are ready',
  'home.footer.emergency.title': 'Medical emergency',
  'home.footer.emergency.body': 'Go to the nearest Emergency Room.',
  'home.footer.emergency.action': 'Call 911',
  'home.greeting.line': '{greeting}, {name}',
  'home.header.book': 'Book a visit',
  'home.block.toFinishUp': 'To finish up',
  'home.empty.title': 'All caught up!',
  'home.greeting.afternoon': 'Good afternoon',
  'home.greeting.evening': 'Good evening',
  'home.greeting.morning': 'Good morning',
  'home.header.notifications.aria': 'Notifications',
  'home.header.notifications.hint': 'Press to view notifications',
  'home.header.profile.aria': "{name}'s profile",
  'home.header.profile.hint': 'Press to go to account settings',
  'home.scheduleUnavailable.message': 'Based on your recent answers, scheduling is temporarily unavailable. Please try again in 24 hours.',
  'home.scheduleUnavailable.aria': 'Schedule unavailable',
  'home.scheduleUnavailable.close': 'close alert',
  'home.hivExposure.dialog.title': 'Have you been exposed to HIV?',
  'home.hivExposure.dialog.description':
    'If you have been potentially exposed to HIV within the past 72 hours, you can meet with your provider to determine if PEP is right for you. Starting PEP medication as soon as possible is important to prevent an HIV infection.',
  'home.hivExposure.dialog.confirm': 'Book an Appointment',
  'home.hivExposure.fab.aria': 'Report potential HIV exposure and book an appointment',
  'home.todos.card.a11y.label': 'View details for {title}',
  'home.todos.appointment.join': 'Join {title}',
  'home.todos.appointment.title': '{reason} Appointment',
  'home.todos.assessment.continue': 'Continue Assessment',
  'home.todos.assessment.description.general': 'Complete health assessment for appointment',
  'home.todos.assessment.description.glp1': 'Complete a new health questionnaire for your GLP-1 treatment.',
  'home.todos.assessment.description.prep': 'Review medication and side effects',
  'home.todos.assessment.start': 'Start Assessment',
  'home.todos.assessment.title.glp1': 'GLP-1 Health Assessment',
  'home.todos.asyncMessaging.continue': 'Continue',
  'home.todos.asyncMessaging.progress': 'Step {current} of {total}: {step}',
  'home.todos.asyncMessaging.description':
    'Your provider can review your request as soon as you submit it. Pick up right where you left off.',
  'home.todos.asyncMessaging.resume': 'Continue your online visit',
  'home.todos.asyncMessaging.start': 'Start',
  'home.todos.asyncMessaging.stepsLeft': '{count, plural, one {# step left} other {# steps left}}',
  'home.todos.asyncMessaging.submitted.description': 'A provider is reviewing your information and will follow up during business hours.',
  'home.todos.asyncMessaging.submitted.status': 'In review',
  'home.todos.asyncMessaging.submitted.title': 'Visit submitted',
  'home.todos.asyncMessaging.title': 'Finish your online visit',
  'home.todos.gileadApplication.description': 'Complete Gilead Advancing Access application',
  'home.todos.gileadApplication.title': 'Gilead Application',
  'home.todos.gileadApplication.view': 'View Gilead Application {id}',
  'home.todos.hivSelfTest.description': 'Complete at-home test and upload results',
  'home.todos.hivSelfTest.dialog.notReceived': 'No, I did not receive it',
  'home.todos.hivSelfTest.dialog.question': 'Did you receive your HIV self-test kit?',
  'home.todos.hivSelfTest.dialog.received': 'Yes, I received it',
  'home.todos.hivSelfTest.dialog.title': 'HIV Self Test',
  'home.todos.hivSelfTest.open': 'Open HIV self-test instructions',
  'home.todos.hivSelfTest.replacement.button': 'Request New Test',
  'home.todos.hivSelfTest.replacement.message':
    "Your Provider's patient services representative will be reaching out as soon as possible to coordinate ordering a replacement test. We apologize for the inconvenience!",
  'home.todos.hivSelfTest.replacement.success': 'Request submitted successfully',
  'home.todos.hivSelfTest.replacement.title': 'Sorry you did not receive your test!',
  'home.todos.hivSelfTest.title': 'HIV Self-Test Kit',
  'home.todos.initialAppointment.description.generic': 'Start {providerType} care appointment',
  'home.todos.initialAppointment.description.hivContinue': 'Continue HIV+ care appointment',
  'home.todos.initialAppointment.description.hivInitial': 'Initial appointment for HIV+ care',
  'home.todos.initialAppointment.description.injectable': 'Consultation for Injectable PrEP options',
  'home.todos.initialAppointmentV2.description': 'Select a service that is right for you',
  'home.todos.initialAppointment.description.prep': 'Initial consultation to start PrEP treatment',
  'home.todos.initialAppointment.description.sti': 'Discuss STI testing options',
  'home.todos.initialAppointment.schedule': 'Schedule appointment',
  'home.todos.initialAppointment.title': 'Book Appointment',
  'home.todos.insurance.cardReady': 'Have your insurance card ready (BIN, PCN, ID, RxGroup).',
  'home.todos.insurance.contact': 'Contact {name} at',
  'home.todos.insurance.description': 'Update your insurance information to ensure continuous coverage.',
  'home.todos.insurance.title': 'Insurance Verification',
  'home.todos.labFlow.description.general': 'Complete lab work to continue care',
  'home.todos.labFlow.description.quarterly': 'Complete quarterly renewal for PrEP',
  'home.todos.labFlow.open': 'Open lab flow',
  'home.todos.labFlow.title': 'Lab Work',
  'labs.kit.instructions.body':
    "When you're ready to return your completed lab kit, please take it inside your local USPS Post Office and hand it directly to a postal employee. Ask them to scan the prepaid return package at the counter before you leave. This initial scan activates the tracking information so both you and our team can monitor your kit's return.\n\nWhenever possible, avoid placing your kit in a USPS drop box or leaving it for your mail carrier to pick up. Those packages are often not scanned when first collected, which can delay tracking or, in some cases, result in the package being lost in transit.\n\nTaking a minute to have your package scanned at the Post Office helps ensure your sample arrives at the lab as quickly and reliably as possible.",
  'labs.kit.instructions.title': 'Lab Kit Return Instructions',
  'home.todos.newDocument.description': 'Review uploaded documents and forms',
  'home.todos.newDocument.title': 'Documents',
  'home.todos.newDocument.view': 'View new documents',
  'home.todos.photoIdUpdate.description': 'Upload a photo of your government-issued ID to verify your identity',
  'home.todos.photoIdUpdate.title': 'ID Verification',
  'home.todos.photoIdUpdate.view': 'Upload photo ID',
  'home.todos.passcodeSetup.description': 'Create a passcode for faster and more convenient login access',
  'home.todos.passcodeSetup.title': 'Passcode Setup',
  'home.todos.passcodeSetup.view': 'Set up passcode',
  'home.todos.biometricSetup.description.faceId': 'Experience safe, secure and seamless login using Face ID',
  'home.todos.biometricSetup.description.fingerprint': 'Experience safe, secure and seamless login using your fingerprint',
  'home.todos.biometricSetup.description.biometric': 'Experience safe, secure and seamless login using biometric authentication',
  'home.todos.biometricSetup.title': 'Biometric Login',
  'home.todos.biometricSetup.view': 'Set up biometric login',
  'home.todos.notificationSetup.description': 'Get instant updates about appointments, messages, and important health information',
  'home.todos.notificationSetup.title': 'Enable Notifications',
  'home.todos.notificationSetup.view': 'Enable notifications',
  'home.todos.establishRecovery.title': 'Finish Establishing Your Care',
  'home.todos.establishRecovery.description': "Your visit didn't take place. Start an online visit now or schedule one for later.",
  'home.todos.establishRecovery.a11y.label': 'Choose how to complete your establish care visit',
  'home.todos.onDemandAppointment.description': 'Continue video consultation with provider',
  'home.todos.onDemandAppointment.rejoin': 'Rejoin appointment',
  'home.todos.onDemandAppointment.title': 'Video Appointment',
  'home.todos.restartCare.addressVerification': 'Please verify that your address information is up to date so we can continue your care.',
  'home.todos.restartCare.description': 'Update address and insurance to continue care',
  'home.todos.restartCare.success.message': 'Our support team will reach out within 24 hours.',
  'home.todos.restartCare.success.title': 'Request Submitted',
  'home.todos.restartCare.title': 'Restart Care',
  'home.todos.restartCare.welcome.message':
    "We noticed it's been a while since your last prescription. Would you like to restart your care with us?\n\nRestarting your care will allow you to get new prescriptions and continue receiving our services.",
  'home.todos.restartCare.welcome.restart': 'Restart Care',
  'home.todos.restartCare.welcome.skip': 'Skip for now',
  'home.todos.restartCare.welcome.title': 'Welcome Back!',
  'home.todos.rescheduleAppointment.title': 'Reschedule Visit',
  'home.todos.rescheduleAppointment.description': 'Your visit has been cancelled. Please reschedule to continue your care.',
  'home.todos.rescheduleAppointment.a11y.label': 'Reschedule your cancelled visit',
  'home.todos.paymentFailure.accessibility': 'Retry your payment that was unsuccessful',
  'home.todos.paymentFailure.description':
    "Your recent payment didn't go through. This could be due to insufficient funds, an expired card, or a billing address mismatch.\n\nPlease retry your payment to continue with your appointment or service.",
  'home.todos.paymentFailure.title': 'Payment Unsuccessful',
  'home.todos.roiRequest.description': 'Complete Release of Information form',
  'home.todos.roiRequest.submit': 'Submit ROI Request',
  'home.todos.roiRequest.title': 'ROI Request #{id}',
  'home.todos.profileIncomplete.title': 'Complete Your Profile',
  'home.todos.profileIncomplete.description':
    'Add remaining details to your profile. Your provider will use this information during your visit.',
  'home.todos.profileIncomplete.a11y.label': 'Press to complete your information',
  'home.todos.status.available': 'Available',
  'home.todos.status.dueNow': 'Due now',
  'home.todos.status.inProgress': 'In Progress',
  'home.todos.status.joinNow': 'Join Now',
  'home.todos.status.new': 'New',
  'home.todos.status.pending': 'Pending',
  'home.todos.status.recommended': 'Recommended',
  'home.todos.status.required': 'Required',
  'home.todos.status.startingSoon': 'Starting Soon',
  'home.todos.status.upcoming': 'Upcoming',
  'home.footer.questions': 'Have a question?',
  'home.footer.faq': 'Read the FAQ',
  'home.footer.faq.aria': 'Frequently Asked Questions',
  'home.footer.emergency.link.aria': 'Call emergency services',
  'home.tour.welcome.title': 'Welcome to {name}',
  'home.tour.welcome.tips': 'Here are a few tips to help you get started.',
  'home.tour.welcome.todoList': 'Your visits, lab results and anything we need from you show up on this screen.',
  'home.tour.welcome.activities': 'We will keep it up to date as your care moves along.',
  'home.tour.appointment.next': 'This is your next appointment.',
  'home.tour.appointment.notification': 'We will send you a reminder before it starts, with a link to join.',
  'home.tour.initialAppointment.book': 'Start here to book your first visit.',
  'home.tour.nav.home': 'Home is your starting point. Your visits, labs and anything we need from you show up here.',
  'home.tour.nav.health': 'Health has your appointments, lab results, prescriptions, documents and assessments.',
  'home.tour.nav.messages': 'Messages is where you talk to your care team. New replies show a badge here.',
  'home.tour.nav.profile': 'Profile is where you update your details and insurance. You will also find F.A.Q. and support here.',

  // Security - Passcode Setup
  'security.passcode.screen.a11y.label': 'Passcode setup screen',
  'security.passcode.create.title': 'Create Your Passcode',
  'security.passcode.create.description': 'Enter a 6-digit passcode for secure and convenient login',
  'security.passcode.confirm.title': 'Confirm Your Passcode',
  'security.passcode.confirm.description': 'Enter your passcode again to confirm',
  'security.passcode.confirm.goBack': 'Start Over',
  'security.passcode.input.a11y.label': 'Enter passcode',
  'security.passcode.error.mismatch': 'Passcodes do not match. Please try again.',
  'security.passcode.error.failed': 'Failed to set passcode. Please try again.',
  'security.passcode.success.title': 'Passcode Created!',
  'security.passcode.success.description': 'You can now use this passcode to log in to your account',

  // Security - Lock Screen
  'security.lock.title': 'Welcome back!',
  'security.lock.error.invalid': 'Incorrect passcode. Please try again.',
  'security.lock.error.invalidWithAttempts': 'Incorrect passcode. {attempts} attempts remaining.',
  'security.lock.error.tooManyAttempts': 'Too many incorrect attempts',
  'security.lock.logout': 'Log out',
  'security.lock.forgotPasscode': 'Forgot Passcode?',
  'security.lock.forgotPasscode.alert.title': 'Forgot Passcode?',
  'security.lock.forgotPasscode.alert.message': 'You can still log in using your email. This will log you out of the app.',
  'security.lock.forgotPasscode.alert.goBack': 'Go Back',
  'security.lock.forgotPasscode.alert.continue': 'Continue',
  'security.lock.biometric.prompt': 'Authenticate to unlock',

  // Security - Biometric Setup
  'security.biometric.title.faceId': 'Enable Face ID',
  'security.biometric.title.fingerprint': 'Enable Fingerprint',
  'security.biometric.title.biometric': 'Enable Biometric Login',
  'security.biometric.description.faceId': 'Use Face ID for quick and secure access to your account',
  'security.biometric.description.fingerprint': 'Use your fingerprint for quick and secure access to your account',
  'security.biometric.description.biometric': 'Use biometric authentication for quick and secure access to your account',
  'security.biometric.enable': 'Enable',
  'security.biometric.skip': 'Skip',
  'security.biometric.prompt.message': 'Authenticate to enable biometric login',

  // Notifications Setup
  'notifications.setup.title': 'Stay Connected',
  'notifications.setup.description':
    'Enable push notifications to receive timely updates about:\n\n• Upcoming appointments\n• New messages from your care team\n• Lab results and prescription updates\n• Important health reminders',
  'notifications.setup.enable': 'Enable Notifications',
  'notifications.setup.denied.title': 'Notifications Are Disabled',
  'notifications.setup.denied.description':
    'You have previously denied notification permissions. To enable notifications, you need to update your settings.',
  'notifications.setup.denied.openSettings': 'Open Settings',
  'notifications.setup.success.title': 'Notifications Enabled!',
  'notifications.setup.success.description': "You'll now receive important updates about your care",

  // Chat/Messaging (Singular)
  'chat.a11y.conversation': 'Conversation with {name}, {unreadStatus}',
  'chat.badge.a11y.noUnread': 'No unread messages',
  'chat.badge.a11y.unread': '{count} unread messages',
  'chat.container.a11y.label': 'Chat conversation area',
  'chat.conversation.a11y.default': 'Chat conversation',
  'chat.conversation.a11y.label': 'Chat with {name}',
  'chat.date.separator.a11y.label': 'Messages from {date}',
  'chat.date.today': '[Today]',
  'chat.date.yesterday': '[Yesterday]',
  'chat.details.participant.a11y.label': '{name}, {role}',
  'chat.details.participants': 'Participants',
  'chat.details.startChat.a11y.label': 'Chat with {name}',
  'chat.disclaimer.title': 'Important Notice',
  'chat.disclaimer.cbo.message': 'This chat is not monitored frequently.\nIf you have questions, please contact Support.',
  'chat.disclaimer.provider.message':
    'We are available Monday to Friday, 8am - 6pm EST.\nResponses may take 1–2 days during business hours.',
  'chat.disclaimer.ma.message':
    'Patient Services Representative business hours are Mon-Fri 8am - 6pm EST. It may take up to 1-2 days to respond to your message if it was sent during regular business hours.',
  'chat.header.viewDetails.a11y.hint': 'Opens the conversation details screen',
  'chat.header.viewDetails.a11y.label': 'View conversation details',
  'chat.input.a11y.hint': 'Type your message here',
  'chat.input.a11y.label': 'Message input',
  'chat.input.inaccessible': "This conversation is not accessible anymore. You can't send messages.",
  'chat.input.inaccessible.a11y.label': 'Inaccessible chat notification',
  'chat.input.placeholder': 'Type a message...',
  'chat.input.readonly': 'Read only. This chat is no longer available.',
  'chat.input.readonly.a11y.label': 'Read-only chat notification',
  'chat.input.send.a11y.hint': 'Sends the typed message',
  'chat.input.send.a11y.label': 'Send message',
  'chat.input.error.archived': 'This conversation has been archived. No new messages can be sent.',
  'chat.input.error.sendFailed': 'Unable to send message. Please try again.',
  'chat.input.error.outOfOffice': "Your message wasn't sent. This team member is out of office right now. Please try again later.",
  'chat.outOfOffice.blocked': "{name} is out of office and isn't receiving new messages right now.",
  'chat.outOfOffice.help': 'Choose an option below to get help.',
  'chat.outOfOffice.user': '{name} is out of office',
  'chat.outOfOffice.a11y.label': 'Out of office notice',
  'chat.message.a11y.label': '{sender} sent a message at {time}',
  'chat.message.bubble.a11y.mine': 'Your message sent at {time}',
  'chat.message.bubble.a11y.other': 'Message from {sender} at {time}',
  'chat.message.call.a11y.hint': 'Calls the support phone number',
  'chat.message.call.a11y.label': 'Call support',
  'chat.message.call.number': '(888) 708-0561',
  'chat.message.sentBy': 'Sent by {name}',
  'chat.message.you': 'You',
  'chat.messages.empty': 'No messages yet. Start the conversation!',
  'chat.messages.list.a11y.label': 'Chat message history',
  'chat.newChat.a11y.hint': 'Opens the new chat screen',
  'chat.newChat.a11y.label': 'Start new chat',
  'chat.noUnread': 'no unread messages',
  'chat.reply': 'Reply',
  'chat.reply.a11y.label': 'Reply to conversation',
  'chat.search.placeholder': 'Search conversations...',
  'chat.send.title': 'Send a message',
  'chat.select.assistantDescription': 'Help with existing prescriptions & lab issues',
  'chat.select.assistantTitle': 'Patient Services Representative',
  'chat.select.modal.title': 'Start a Conversation',
  'chat.select.providerDescription': 'Questions about your care, medication changes, or medical advice',
  'chat.select.providerTitle': 'My Provider',
  'chat.select.screen.a11y.label': 'Select who to chat with',
  'chat.select.subtitle': 'Choose from your healthcare team members below',

  // Messages Page
  'messages.threads.ariaLabel': 'Message threads',
  'messages.blank.title': 'Start meaningful conversations!',
  'chat.select.supportDescription': 'Help with restarting care, insurance, copay, address change',
  'chat.select.supportTitle': 'Customer Support',
  'chat.select.title': 'Who would you like to chat with?',
  'chat.support.businessHours':
    'Customer Support business hours are Mon-Fri 8am - 6pm EST. It may take up to 1-2 days to respond to your message if it was sent during regular business hours.',
  'chat.support.empty': 'Need help with restarting care, insurance, copays, address changes, or prescription questions?',
  'chat.support.request.a11y.label': 'Support chat request screen',
  'chat.title': 'Chats',
  'chat.unreadCount': '{count} unread messages',
  'chat.user.request.a11y.label': 'User chat request screen',
  'chat.userHeader.viewDetails.a11y.hint': 'Opens the user details screen',
  'chat.userHeader.viewDetails.a11y.label': 'View {name} details',

  // Chat Dialog - Message Dialogs

  // Chat/Messaging (Plural)
  'chats.empty.default.description': 'Start connecting with your healthcare team. Your messages and important updates will appear here.',
  'chats.empty.default.title': 'Your conversation hub awaits!',
  'chats.empty.search.description': 'We couldn\'t find any chats matching "{search}". Try a different search term!',
  'chats.empty.search.title': 'No conversations found',

  // Async Messaging
  'asyncMessaging.aboutMe.description':
    'A few questions about you. Your answers are private and help your provider give you the right care.',
  'asyncMessaging.assessment.continue': 'Continue',
  'asyncMessaging.assessment.estimatedTime': 'About 30 quick questions · 5–10 min',
  'asyncMessaging.assessment.progress': 'Progress: {progress}%',
  'asyncMessaging.assessment.start': 'Start',
  'asyncMessaging.assessment.aria.start': 'Start assessment: {name}',
  'asyncMessaging.assessment.aria.continue': 'Continue assessment: {name}',
  'asyncMessaging.assessment.aria.canceled': 'Assessment canceled: {name}',
  'asyncMessaging.assessment.aria.view': 'View assessment: {name}',
  'asyncMessaging.assessment.status.inProgress': 'In Progress',
  'asyncMessaging.assessment.status.canceled': 'Canceled',
  'asyncMessaging.assessment.status.completed': 'Complete',
  'asyncMessaging.assessment.status.unknown': 'Assessment status: {status}',
  'asyncMessaging.cancel.action': 'Cancel visit request',
  'asyncMessaging.completed.description1': 'Thanks! A provider will review your answers and reach out if they have any questions.',
  'asyncMessaging.completed.description2':
    'We usually reply within 24 hours. On weekends and holidays, expect to hear from us by the next business day.',
  'asyncMessaging.completed.nextStep1': 'Your information will be reviewed by our medical team',
  'asyncMessaging.completed.nextStep2': "You'll get an email when your provider replies",
  'asyncMessaging.completed.nextStep3': 'A provider may contact you for additional information if needed',
  'asyncMessaging.completed.nextStepsTitle': 'What happens next?',
  'asyncMessaging.completed.pageTitle': 'Visit Submitted',
  'asyncMessaging.completed.returnDashboard': 'Go to Home',
  'asyncMessaging.completed.title': "You're all set!",
  'asyncMessaging.healthHistory.add': 'Add',
  'asyncMessaging.healthHistory.collapse': 'Collapse',
  'asyncMessaging.healthHistory.description':
    'Add any allergies, medications, conditions, surgeries or immunizations you have. If you have none, just tick the box.',
  'asyncMessaging.healthHistory.expand': 'Expand',
  'asyncMessaging.healthHistory.expandLabel': '{action} {title} section',
  'asyncMessaging.healthHistory.item': 'item',
  'asyncMessaging.healthHistory.itemCount': '{count} {pluralizedItem}',
  'asyncMessaging.healthHistory.items': 'items',
  'asyncMessaging.healthHistory.loading': 'Loading...',
  'asyncMessaging.photoId.description':
    "Take a clear photo of your driver's license or other government ID. We use it to confirm it's you before prescribing. It stays private.",
  'asyncMessaging.photoId.uploadContinue': 'Continue',
  'asyncMessaging.stepper.steps.aboutMe': 'About Me',
  'asyncMessaging.stepper.steps.assessment': 'Health questions',
  'asyncMessaging.stepper.steps.healthHistory': 'Health History',
  'asyncMessaging.stepper.steps.insurance': 'Insurance',
  'asyncMessaging.stepper.steps.photoId': 'Photo ID',
  'asyncMessaging.stepper.steps.vitals': 'Vitals',
  'asyncMessaging.primaryCare.description':
    'Please provide your primary care provider information. This helps us coordinate your care and ensure continuity of treatment.',
  'asyncMessaging.stepper.steps.primaryCare': 'Primary Care',
  'asyncMessaging.stepper.steps.completion': 'Complete',
  'asyncMessaging.stepper.steps.video': 'Finish',
  'asyncMessaging.establish.confirmation.title': 'Almost there! One more step to establish your care.',
  'asyncMessaging.establish.confirmation.description':
    'A brief 5-minute appointment is required to establish care. You must schedule this appointment before care can continue.',
  'asyncMessaging.establish.hivInitial.confirmation.description':
    'A video appointment is required to establish care. You must schedule this appointment before care can continue.',
  'asyncMessaging.establish.confirmation.action': 'Schedule',
  'asyncMessaging.establish.video.instruction':
    'Watch the video below to learn about your care, then schedule your required 5-minute establish care appointment.',
  'asyncMessaging.establish.video.description':
    'This video explains our telehealth and establish care process. After watching, you will be prompted to schedule a brief appointment.',
  'asyncMessaging.vitals.description': 'Add your current height and weight. Blood pressure and heart rate help too, if you know them.',
  'asyncMessaging.confirmation.title': 'Review your details and confirm to submit your async visit request.',
  'asyncMessaging.confirmation.description':
    'By completing this request, you confirm the information provided is accurate to the best of your knowledge.',
  'asyncMessaging.progress.step': 'Step {current} of {total}',
  'asyncMessaging.progress.lastStep': 'Last step',
  'asyncMessaging.progress.next': 'Next: {step}',
  'asyncMessaging.vitals.hint': 'Add your {vitals} to continue',
  'asyncMessaging.healthHistory.hint': 'Add an item or confirm you have none for {sections} to continue',
  'asyncMessaging.healthHistory.noneOfThese': "I don't have any of these",
  'asyncMessaging.healthHistory.noneOfRemaining': "I don't have any of the other {count}",
  'asyncMessaging.insurance.optionalHint': 'Insurance is optional. You can add it later from your profile.',
  'asyncMessaging.insurance.requiredHint': 'Add your insurance to continue',
  'asyncMessaging.insurance.skip': 'Skip for now',
  'asyncMessaging.title': '{type} Online Visit',
  'asyncMessaging.video.title': 'Educational Video: Understanding Telehealth',
  'asyncMessaging.video.description': 'This video explains our telehealth and async care process.',
  'asyncMessaging.video.checkboxLabel': 'I watched the video and understand how online visits work',
  'asyncMessaging.video.instruction':
    'To complete your async visit request: watch the video below, check the confirmation box, and tap "Complete".',

  // Notifications
  'notifications.badge.a11y.noUnread': 'No unread notifications',
  'notifications.badge.a11y.unread': '{count} unread notifications',
  'notifications.empty.description': "You're all caught up! New notifications will appear here.",
  'notifications.empty.title': 'No notifications',
  'notifications.item.a11y.label': '{title}. {message}. {timeAgo}. {readStatus}',
  'notifications.list.a11y.label': 'List of notifications',
  'notifications.preferences.save.a11y.hint': 'Press to save your notification settings',
  'notifications.preferences.save.a11y.label': 'Save notification preferences',
  'notifications.preferences.screen.a11y.label': 'Notification preferences screen',
  'notifications.preferences.select.a11y.hint': 'Choose how you want to receive notifications',
  'notifications.preferences.select.a11y.label': 'Select preferred communication method',
  'notifications.timeAgo.days': '{count}d ago',
  'notifications.timeAgo.hours': '{count}h ago',
  'notifications.timeAgo.minutes': '{count} min ago',
  'notifications.timeAgo.now': 'Just now',
  'notifications.timeAgo.weeks': '{count}w ago',
  'notifications.title': 'Notifications',
  'notifications.toast.close': 'Close notification',
  'notifications.toast.joinAppointment.title': 'Join Appointment',
  'notifications.toast.joinAppointment.description': 'Your scheduled appointment is about to start. Please press here to enter.',
  'notifications.toast.pressToView': 'Press to view',

  // Visit (Video Visit/Twilio Components)
  'visit.completed.heading': 'Your video appointment was completed.',
  'visit.completed.messagePrompt': 'If you need to speak with your provider you can send them a message.',
  'visit.failed.heading': "We're sorry but your video meeting isn't available.",
  'visit.failed.description':
    "Your appointment was not completed. This may have been due to a technical issue, or because we couldn't provide the care you were looking for.",
  'visit.inProgress.joining': 'Joining...',
  'visit.ended.heading': 'You left the call',
  'visit.ended.rejoin': 'Rejoin call',
  'visit.call.duration.aria': 'Call duration {time}',
  'visit.call.status.cameraOff': 'Your camera is off',
  'visit.call.status.micOff': 'Your microphone is muted',
  'visit.pip.swap.aria': 'Swap main video',
  'visit.waiting.screen.title': 'Waiting Room',
  'visit.controls.flip.label': 'Flip',
  'visit.controls.camera.label': 'Camera',
  'visit.controls.mute.label': 'Mute',
  'visit.controls.unmute.label': 'Unmute',
  'visit.controls.speaker.label': 'Speaker',
  'visit.controls.end.label': 'End',
  'visit.controls.chat.label': 'Chat',
  'visit.loading': 'Preparing your healthcare experience...',
  'visit.missed.heading': "We're sorry but your scheduled appointment was not completed.",
  'visit.unavailable.heading': "We're sorry but your video meeting isn't available.",
  'visit.unavailable.description': 'You can only join a scheduled appointment that is pending.',
  'visit.waiting.providerWillBeWithYou': '{providerName} will be with you shortly.',
  'visit.waiting.willBeWithYouShortly': 'will be with you shortly',
  'visit.waiting.status.waiting': 'Waiting…',
  'visit.waiting.cameraCheck.cameraOn': 'Camera on',
  'visit.tips.short.access': 'Access allowed',
  'visit.tips.short.camera': 'Camera working',
  'visit.tips.short.wifi': 'Strong wi-fi',
  'visit.tips.short.headphones': 'Use headphones',
  'visit.actions.finishEstablishingCare': 'Finish Establishing Your Care',
  'visit.actions.bookNewAppointment': 'Book new Appointment',
  'visit.actions.checkAudioVideo': 'Check your audio and video',
  'visit.actions.goToHomePage': 'Go to my home page',
  'visit.actions.messageProvider': 'Message Provider',
  'visit.tips.beforeYourCall': 'Before your call',
  'visit.tips.heading': 'Tips for having a great call',
  'visit.tips.checkCamera': 'Check that camera and audio are working',
  'visit.tips.quietPlace': 'Go to a quiet place',
  'visit.tips.allowAccess': 'Allow access to camera and microphone',
  'visit.tips.useHeadphones': 'Use headphones',
  'visit.tips.strongWifi': 'Have a strong wifi connection',
  'visit.controls.endCall': 'End Call',
  'visit.controls.endCall.dialog.title': 'Leave video?',
  'visit.controls.endCall.dialog.description':
    'Warning! Leaving the video will end the call with the provider and you will not be able to rejoin.',
  'visit.controls.endCall.dialog.leave': 'Leave',
  'visit.controls.endCall.dialog.continue': 'Continue Video Call',
  'visit.controls.endCall.error': 'Unable to complete the call. Please try again.',
  'visit.controls.audio.mute': 'Mute',
  'visit.controls.audio.unmute': 'Unmute',
  'visit.controls.audio.noAudio': 'No Audio',
  'visit.controls.video.start': 'Start Video',
  'visit.controls.video.stop': 'Stop Video',
  'visit.controls.video.noVideo': 'No Video',
  'visit.controls.camera.flip': 'Flip Camera',
  'visit.controls.chat.toggle': 'Toggle Chat',
  'visit.settings.device.title': 'Audio and Video Settings',
  'visit.settings.device.microphone': 'Microphone',
  'visit.settings.device.microphone.select': 'Select microphone',
  'visit.settings.device.microphone.noLocal': 'No Local Audio',
  'visit.settings.device.speakers': 'Speakers',
  'visit.settings.device.speakers.select': 'Select speakers',
  'visit.settings.device.speakers.default': 'System Default Audio Output',
  'visit.settings.device.camera': 'Camera',
  'visit.settings.device.camera.select': 'Select camera',
  'visit.settings.device.camera.noLocal': 'No Local Video',
  'visit.error.title': 'Error',
  'visit.error.code': 'Code: {code}',
  'visit.error.invalidToken': 'Please make sure you are using the correct credentials.',
  'visit.error.screenSharePermission':
    'Unable to share your screen. Please make sure that your operating system has the correct permissions enabled for screen sharing.',
  'visit.participant.videoOf': 'Video of {name}',
  'visit.participant.you': ' (You)',
  'visit.participant.screen': ' - Screen',
  'visit.participant.mainParticipant': 'Main participant: {name}',
  'visit.participant.reconnecting': 'Reconnecting...',
  'visit.participant.aria': 'Participant: {name}',
  'visit.participant.screenSharing': 'Screen sharing',
  'visit.participant.selected': 'Selected participant',
  'visit.participant.list': 'Participants List',
  'visit.recording.label': 'Recording',
  'visit.recording.tooltip': "All participants' audio and video is currently being recorded. Visit the app settings to stop recording.",
  'visit.permissions.welcome': 'Hi {firstName},',
  'visit.permissions.welcomeMessage': 'Welcome to your {appName} video meeting.',
  'visit.permissions.accessRequired': 'To have a video meeting you need to allow access to your camera and microphone.',
  'visit.permissions.error.title': "Something isn't working :(",
  'visit.permissions.error.description': 'Your camera and microphone need to be enabled for the meeting.',
  'visit.permissions.success.title': 'All set!',
  'visit.permissions.success.description': 'Your camera and microphone are ready. You can now join your video appointment.',
  'visit.permissions.camera': 'Camera',
  'visit.permissions.microphone': 'Microphone',
  'visit.permissions.continueToAppointment': 'Continue to appointment',
  'visit.permissions.required.title': 'Permission Required',
  'visit.permissions.required.description': 'We need access to your camera and microphone to start your video appointment.',
  'visit.permissions.initial.title': 'Camera & Microphone Access',
  'visit.permissions.initial.description': 'To join your video appointment, we need permission to access your camera and microphone.',
  'visit.permissions.checking': 'Checking permissions...',
  'visit.permissions.privacy.title': 'Your privacy is protected',
  'visit.permissions.privacy.description':
    'Your browser will ask for permission. Click "Allow" to enable video meetings. You can always change these settings later.',
  'visit.permissions.allowButton': 'Allow access',
  'visit.permissions.status.allowed': 'Allowed',
  'visit.permissions.status.blocked': 'Blocked',
  'visit.permissions.status.unknown': 'Unknown',
  'visit.permissions.enable.title': 'Enable Permissions',
  'visit.permissions.enable.description':
    'Please allow camera and microphone access when prompted by your browser. You can also check your browser settings or operating system privacy settings if permissions are blocked.',
  'visit.menu.openMenu': 'Open menu',
  'visit.menu.mainControls': 'Main controls',
  'visit.menu.audioVideoSettings': 'Audio and Video Settings',
  'visit.screenShare.sharing': 'You are sharing your screen',
  'visit.screenShare.stop': 'Stop Sharing',
  'visit.screenShare.stopAria': 'Stop sharing your screen',
  'visit.screenShare.start': 'Share Screen',
  'visit.screenShare.stopShare': 'Stop Screen Share',
  'visit.screenShare.startAria': 'Start screen sharing',
  'visit.screenShare.stopShareAria': 'Stop screen sharing',
  'visit.screenShare.tooltip.anotherUser': 'Cannot share screen when another user is sharing',
  'visit.screenShare.tooltip.notSupported': 'Screen sharing is not supported with this browser',
  'visit.network.quality.unknown': 'Unknown',
  'visit.network.quality.veryPoor': 'Very Poor',
  'visit.network.quality.poor': 'Poor',
  'visit.network.quality.average': 'Average',
  'visit.network.quality.good': 'Good',
  'visit.network.quality.excellent': 'Excellent',
  'visit.network.quality.aria': 'Network quality is {quality}',
  'visit.network.quality.title': 'Network quality: {quality}',
  'visit.notification.reconnecting': 'Connection Lost: Reconnecting to room...',
  'visit.notification.recordingStarted': 'Recording has started.',
  'visit.notification.recordingInProgress': 'Recording is in progress.',
  'visit.notification.recordingFinished':
    'Recording Complete, you can view the recording in the Twilio Console. Recordings will be available once this room has ended.',
  'visit.call.notification.title': 'Visit in progress',
  'visit.call.notification.text': 'Tap to return to your video visit.',

  // Document Management
  'documents.author.system': 'System',
  'documents.camera.error.message': 'Unable to access camera. Please try again.',
  'documents.camera.error.title': 'Camera Error',
  'documents.card.viewDocument': 'View document: {name}',
  'documents.document.error.message': 'Unable to select file. Please try again.',
  'documents.document.error.title': 'File Selection Error',
  'documents.empty.description': 'All your important medical documents will be safely stored here once they become available',
  'documents.empty.title': 'Your document library is getting ready!',
  'documents.loadMore.ariaLabel': 'Load more documents',
  'documents.loadMore.button': 'more',
  'documents.fallback.name': 'Document',

  'documents.gallery.error.message': 'Unable to access photo library. Please try again.',
  'documents.gallery.error.title': 'Gallery Error',
  'documents.lab.restriction.message': 'Please complete all required items in your profile before uploading lab work.',
  'documents.lab.restriction.title': 'Profile Incomplete',
  'documents.mimetype.audio': 'Audio',
  'documents.mimetype.excel': 'Excel Spreadsheet',
  'documents.mimetype.gif': 'GIF Image',
  'documents.mimetype.jpeg': 'JPEG Image',
  'documents.mimetype.pdf': 'PDF',
  'documents.mimetype.png': 'PNG Image',
  'documents.mimetype.powerpoint': 'PowerPoint',
  'documents.mimetype.text': 'Text File',
  'documents.mimetype.video': 'Video',
  'documents.mimetype.word': 'Word Document',
  'documents.mimetype.zip': 'ZIP Archive',
  'documents.permissions.camera.message': 'Please allow camera access to take photos',
  'documents.permissions.camera.title': 'Camera Permission Required',
  'documents.permissions.photos.message': 'Please allow photo library access to select images',
  'documents.permissions.photos.title': 'Photo Library Permission Required',
  'documents.remove.confirm.message': 'Are you sure you want to remove this file?',
  'documents.remove.failed.a11y': 'Remove failed file {index}',
  'documents.remove.file.a11y': 'Remove {filename}',
  'documents.title': 'Documents',
  'documents.upload.button.a11y': 'Upload document',
  'documents.upload.button.hint': 'Opens upload options for photos and documents',
  'documents.upload.document': 'Browse Files',
  'documents.upload.document.a11y': 'Select document file',
  'documents.upload.document.description': 'Select any file type from your device',
  'documents.upload.files.placeholder': 'Upload files',
  'documents.upload.dropzone.ariaLabel': 'File upload dropzone',
  'documents.upload.dropzone.desktop': 'Click or drag and drop to upload',
  'documents.upload.dropzone.mobile': 'Tap to upload',
  'documents.upload.gallery': 'Photo Library',
  'documents.upload.gallery.a11y': 'Select photo from gallery',
  'documents.upload.gallery.description': 'Choose from your gallery',
  'documents.upload.loading.description': 'Please wait while we upload your document',
  'documents.upload.loading.title': 'Uploading...',
  'documents.upload.options.a11y': 'Document upload options',
  'documents.upload.options.message': 'Choose how you want to add files',
  'documents.upload.options.subtitle': 'Choose how you want to add your document',
  'documents.upload.options.title': 'Upload Options',
  'documents.upload.photos.placeholder': 'Upload photos',
  'documents.upload.placeholder': 'Upload documents',
  'documents.upload.progress': '{count} / {limit} files uploaded',
  'documents.upload.success.message': 'Your document has been uploaded successfully',
  'documents.upload.success.title': 'Upload Successful',
  'documents.upload.uploading': 'Uploading document...',

  // Insurance (Singular)
  'insurance.back.placeholder': 'Upload back of your insurance card',
  'insurance.back.title': 'Back of Insurance Card',
  'insurance.bin.label': 'BIN',
  'insurance.bin.placeholder': 'Enter BIN',
  'insurance.bin.required': 'Please enter your Rx BIN',
  'insurance.company.helper': 'The name of your health insurance company, from your insurance card.',
  'insurance.company.label': 'Insurance Company',
  'insurance.company.placeholder': 'Enter insurance company name',
  'insurance.delete.a11y.hint': 'Press to delete this insurance card',
  'insurance.delete.a11y.label': 'Delete insurance card',
  'insurance.delete.cancel': 'Cancel',
  'insurance.delete.confirm': 'Delete',
  'insurance.delete.message':
    'You may be required to keep active insurance by your community partner, and no insurance may result in issues filling your prescriptions',
  'insurance.delete.title': 'Delete Insurance',
  'insurance.deleted': 'Insurance information deleted successfully',
  'insurance.description': 'Please enter your insurance information below. This helps us process your care correctly.',
  'insurance.edit.screen.a11y.label': 'Edit insurance card screen',
  'insurance.form.a11y.label': 'Insurance information form',
  'insurance.front.placeholder': 'Upload front of your insurance card',
  'insurance.front.title': 'Front of Insurance Card',
  'insurance.groupnum.label': 'GRP',
  'insurance.groupnum.placeholder': 'Enter GRP',
  'insurance.idnum.label': 'ID Number',
  'insurance.idnum.placeholder': 'Enter ID number',
  'insurance.help.button': 'Help with insurance card',
  'insurance.help.button.a11y': 'Get help with finding information on your insurance card',
  'insurance.help.description': 'Your health insurance company will provide you with a card showing your information.',
  'insurance.help.image.alt': 'Example of a health insurance card highlighting key information fields.',
  'insurance.help.instructions': 'The example below shows how to find the information you need to enter.',
  'insurance.help.optionalFields': 'If your card has a PCN, BIN, or GRP number, you can enter those as well.',
  'insurance.help.title': 'Insurance Card Example',
  'insurance.imagesTitle': 'Insurance Card Images',
  'insurance.new.screen.a11y.label': 'Add new insurance card screen',
  'insurance.pcn.label': 'PCN',
  'insurance.pcn.placeholder': 'Enter PCN',
  'insurance.save.a11y.hint': 'Press to save your insurance information',
  'insurance.save.a11y.label': 'Save insurance card',
  'insurance.saved': 'Insurance information saved',
  'insurance.subscriber.helper': 'From your health insurance card. May be shown as member name, name, enrollee, or insured.',
  'insurance.subscriber.label': 'Insured Name',
  'insurance.subscriber.placeholder': 'Enter insured name',
  'insurance.twoColumns.a11y.label': 'Additional insurance details',

  // Insurance (Plural)
  'insurances.card.a11y.hint': 'Press to view or edit this insurance card',
  'insurances.card.a11y.label': '{company} insurance, ID: {id}',
  'insurances.empty.description': 'Keep your coverage info handy and make healthcare visits smoother by adding your insurance cards here.',
  'insurances.empty.title': 'Ready to add your insurance!',
  'insurances.list.a11y.label': 'List of insurance cards',

  // Consent Forms
  'consent.dataSharing.agreement': 'I agree to share my information with {cboName}',
  'consent.dataSharing.defaultPartner': 'our community partner',
  'consent.dataSharing.description': '{cboName}, our community partner, uses your information to waive your provider and lab fees.',
  'consent.dataSharing.error.required': 'Agree to sharing your information',
  'consent.dataSharing.note': 'Some insurance providers may restrict pharmacy choices which can impact participation.',
  'consent.dataSharing.title': 'Data Sharing',
  'consent.header.description': 'Please review and complete the required consents to continue.',
  'consent.policyUpdates.accept': 'I accept the Q Care Plus',
  'consent.policyUpdates.error.required': 'Agree Policy',
  'consent.policyUpdates.label': 'Our policies have been updated. Please review and accept to continue.',
  'consent.policyUpdates.title': 'Policy Updates',
  'consent.treatment.description': 'We require all patients to confirm their Consent to Treatment by Q Care Plus providers.',
  'consent.treatment.error.required': 'Enter your initials',
  'consent.treatment.instructions': 'Initial below to give your consent.',
  'consent.treatment.title': 'Consent to Treat',

  // Release of Information
  'roiRequest.title': 'ROI Request',
  'roiRequest.authorize.authorizeDisclosure.description': 'Authorize Q Care Plus to share your information with another provider',
  'roiRequest.authorize.authorizeDisclosure.title': 'Authorize Disclosure',
  'roiRequest.authorize.decline.message':
    'If you decline to complete the Release of Information (ROI) form, we will be unable to release your medical records.\n\nIf you have questions about Release of Information (ROI) form or would like to request a new form, please contact your patient services representative.',
  'roiRequest.authorize.decline.title': 'Decline ROI Request Form?',
  'roiRequest.authorize.discloseInfoTo.subtitle': 'Please provide the complete details below',
  'roiRequest.authorize.discloseInfoTo.title': 'Send My QCare Records To',
  'roiRequest.authorize.discloseStatement':
    'I hereby authorize Q Care Plus to disclose protected health information concerning the above-named person to: {name}',
  'roiRequest.authorize.form.address.label': 'Address',
  'roiRequest.authorize.form.address.placeholder': 'Enter Address',
  'roiRequest.authorize.form.attention.label': 'Attention',
  'roiRequest.authorize.form.attention.placeholder': 'Enter Attention',
  'roiRequest.authorize.form.fax.label': 'Fax',
  'roiRequest.authorize.form.fax.placeholder': 'Enter Fax',
  'roiRequest.authorize.form.faxEmail.label': 'Fax or Email',
  'roiRequest.authorize.form.faxEmail.placeholder': 'Enter Fax or Email',
  'roiRequest.authorize.form.hospitalAgency.label': 'Name of Hospital/Agency/Clinic/Physician',
  'roiRequest.authorize.form.hospitalAgency.placeholder': 'Enter Name of Hospital/Agency/Clinic/Physician',
  'roiRequest.authorize.form.phone.label': 'Phone',
  'roiRequest.authorize.form.phone.placeholder': 'Enter Phone',
  'roiRequest.authorize.releaseInfoTo.subtitle': 'Please provide the complete details below',
  'roiRequest.authorize.releaseInfoTo.title': 'Request My Records From',
  'roiRequest.authorize.releaseStatement':
    'I hereby authorize {name}, {contact} to release protected health information concerning the above-named person to Q Care Plus',
  'roiRequest.authorize.requestRelease.description': 'Authorize another provider to release your information to Q Care Plus',
  'roiRequest.authorize.requestRelease.title': 'Request Release',
  'roiRequest.authorize.subtitle': "Choose how you'd like to handle your medical information",
  'roiRequest.authorize.title': 'Authorization Type',
  'roiRequest.authorize.validation.authorizationType': 'Please select an authorization type',
  'roiRequest.authorize.validation.phoneLength': 'Please enter valid phone',
  'roiRequest.consent.drugAlcohol.description': 'Participation in any federally assisted drug and alcohol abuse program.',
  'roiRequest.consent.drugAlcohol.title': 'Drug and Alcohol Program',
  'roiRequest.consent.hivAids.description':
    'Information relating to HIV testing, HIV status, or AIDS. I understand that such information is subject to special protections pursuant to state and federal laws and regulations.',
  'roiRequest.consent.hivAids.title': 'HIV/AIDS Information',
  'roiRequest.consent.mentalHealth.description':
    'Information relating to diagnosis and treatment of mental, alcoholic, drug dependency, or emotional condition, other than notes recorded by a mental health professional documenting or analyzing conversation during a counseling session provided such notes are maintained separately (unless this authorization pertains specifically to psychotherapy notes).',
  'roiRequest.consent.mentalHealth.title': 'Mental Health and Substance Abuse',
  'roiRequest.consent.subtitle':
    'I understand that the information to be disclosed may include information relating to the following and I authorize the use or disclosure of this type of information:',
  'roiRequest.consent.title': 'Consent to disclose sensitive information',
  'roiRequest.consent.validation.selectAtLeastOne': 'Select at least one',
  'roiRequest.disclosure.admissionHistory.label': 'Admission History & Physical',
  'roiRequest.disclosure.consultationReports.label': 'Consultation Reports',
  'roiRequest.disclosure.dateRange.from.label': 'From',
  'roiRequest.disclosure.dateRange.from.placeholder': 'Select start date',
  'roiRequest.disclosure.dateRange.title': 'Date Range',
  'roiRequest.disclosure.dateRange.until.label': 'Until',
  'roiRequest.disclosure.dateRange.until.placeholder': 'Select end date',
  'roiRequest.disclosure.demographics.label': 'Demographic Information',
  'roiRequest.disclosure.dischargeSummary.label': 'Discharge Summary',
  'roiRequest.disclosure.entireRecord.label': 'Entire Medical Record',
  'roiRequest.disclosure.imagingReports.label': 'Imaging / Radiology Reports',
  'roiRequest.disclosure.informationTypes.title': 'Information Types',
  'roiRequest.disclosure.labTests.label': 'Lab Test Results',
  'roiRequest.disclosure.nursingNotes.label': 'Nursing Notes',
  'roiRequest.disclosure.operativeReports.label': 'Operative / Procedure Reports',
  'roiRequest.disclosure.other.label': 'Other (specify below)',
  'roiRequest.disclosure.other.textLabel': 'Specify Other Information',
  'roiRequest.disclosure.other.textPlaceholder': 'Please describe the specific information you want to disclose...',
  'roiRequest.disclosure.physicianNotes.label': 'Physician Progress Notes',
  'roiRequest.disclosure.physicianOrders.label': 'Physician Orders',
  'roiRequest.disclosure.specificTypes.subtitle': 'Or select specific types:',
  'roiRequest.disclosure.subtitle': 'Select the date range and types of information to be disclosed',
  'roiRequest.disclosure.title': 'Information to be disclosed',
  'roiRequest.disclosure.validation.otherDetails': 'Please provide details',
  'roiRequest.expiration.defaultInfo':
    'Unless an earlier date is specified, this authorization will expire 12 months from the date of signature below.',
  'roiRequest.expiration.subtitle': 'Select when this authorization should expire',
  'roiRequest.expiration.title': 'Expiration of Authorization',
  'roiRequest.expiration.validUntil.description': 'This authorization is valid until calendar date:',
  'roiRequest.expiration.validUntil.label': 'Valid Until',
  'roiRequest.expiration.validUntil.placeholder': 'Select expiration date',
  'roiRequest.expiration.validation.minDate': 'Must be at least tomorrow',
  'roiRequest.navigation.complete': 'Complete',
  'roiRequest.navigation.decline': 'Decline',
  'roiRequest.sign.authorize.statement':
    'I, the undersigned, have read the above and authorize the disclosure of such health information as described herein. I understand that treatment is not conditioned upon the execution of this authorization. I understand that if I agree to sign this authorization, I have a right to be provided with a signed copy of this form. I understand that I also have the right to inspect and receive a copy of the health information that I have authorized to be disclosed pursuant to this authorization. I understand that the information described above may be subject to re-disclosure by the recipient and may no longer be protected by federal privacy regulations. I understand that I may revoke this authorization at any time, except to the extent that action has been taken in reliance on this authorization, or as otherwise stated in the Notice of Privacy Practices.',
  'roiRequest.sign.date.label': 'Date: {date}',
  'roiRequest.sign.representative.authority.authorizedRepresentative': 'Authorized Representative',
  'roiRequest.sign.representative.authority.guardian': 'Guardian',
  'roiRequest.sign.representative.authority.label': 'Authority to represent individual',
  'roiRequest.sign.representative.authority.parent': 'Parent',
  'roiRequest.sign.representative.authority.powerOfAttorney': 'Power of Attorney',
  'roiRequest.sign.representative.name.label': 'Name of Representative',
  'roiRequest.sign.representative.name.placeholder': 'Enter representative name',
  'roiRequest.sign.revoke.statement':
    'If completed, send copy of entire form to person or organization named in Section B. I no longer want health information pertaining to the person named in Section A shared with the hospital, agency, clinic, and/or physician named in Section B. I understand action already taken before the revocation is received is not affected.',
  'roiRequest.sign.signaturePad.individual.label': 'Signature of Individual',
  'roiRequest.sign.signaturePad.representative.label': 'Signature of Representative',
  'roiRequest.sign.status.label': 'Signature for Medical Record',
  'roiRequest.sign.status.release': 'Release',
  'roiRequest.sign.status.revocation': 'Revocation',
  'roiRequest.sign.subtitle': 'Complete your signature to finalize the authorization',
  'roiRequest.sign.title': 'Signature',
  'roiRequest.sign.user.individual': 'Individual',
  'roiRequest.sign.user.label': 'I am',
  'roiRequest.sign.user.representative': 'Representative',
  'roiRequest.sign.validation.signatureRequired': 'Please provide your signature',
  'roiRequest.steps.authorization': 'Authorization',
  'roiRequest.steps.details': 'Details',
  'roiRequest.steps.reviewSign': 'Review & Sign',
  'roiRequest.success.submitted': 'Application sent for review',

  // Gilead Program Application
  'gileadApplication.contactAuthorization.description':
    'Please specify how Advancing Access can contact you about your benefits and program communications.',
  'gileadApplication.contactAuthorization.detailedMessage.label':
    'I authorize Advancing Access to leave a detailed message, including the name of my prescription, if I am unavailable when they call.',
  'gileadApplication.contactAuthorization.methods.email': 'Email',
  'gileadApplication.contactAuthorization.methods.label':
    'I authorize Advancing Access to provide me with information on my benefits and other communications that contain reference to the Advancing Access program or the ARx Patient Solutions Pharmacy through the following (select all that apply):',
  'gileadApplication.contactAuthorization.methods.mail': 'US mail',
  'gileadApplication.contactAuthorization.methods.phone': 'Phone call',
  'gileadApplication.contactAuthorization.methods.text': 'Text message',
  'gileadApplication.contactAuthorization.note.preference':
    'If I do not select a contact preference, I understand that Advancing Access will provide program communications to me by phone and/or through my healthcare provider',
  'gileadApplication.contactAuthorization.note.textRates':
    'Text message and data rates may apply. You can opt out of such text messages at any time by replying "STOP"',
  'gileadApplication.contactAuthorization.note.title': 'Note',
  'gileadApplication.contactAuthorization.title': 'Contact Authorization',
  'gileadApplication.title': 'Gilead Application',
  'gileadApplication.decline': 'Decline',
  'gileadApplication.decline.confirm.cancel': 'Cancel',
  'gileadApplication.decline.confirm.decline': 'Decline',
  'gileadApplication.decline.confirm.message':
    'If you have questions about Gilead Advancing Access or think something in the application should be changed, please contact customer support.\n\nAnd if you change your mind you can always send a message to Customer Support and they will be glad to prepare a new application.',
  'gileadApplication.decline.confirm.title': 'Decline Gilead AA Program?',
  'gileadApplication.declining': 'Declining...',
  'gileadApplication.incomeHousehold.description':
    'To enroll in the Gilead Advancing Access Program, we need some additional information. Please provide your household income and number of household members.',
  'gileadApplication.incomeHousehold.explanation.label': 'Please explain how you are currently supporting yourself financially below',
  'gileadApplication.incomeHousehold.explanation.placeholder': 'Describe your financial situation',
  'gileadApplication.incomeHousehold.explanation.required':
    'Please tell us how you are currently supporting yourself financially before you sign.',
  'gileadApplication.incomeHousehold.income.invalid': 'Please enter a valid income',
  'gileadApplication.incomeHousehold.income.label': 'Household Income',
  'gileadApplication.incomeHousehold.income.placeholder': 'Enter your household income',
  'gileadApplication.incomeHousehold.members.invalid': 'Please enter a valid household size',
  'gileadApplication.incomeHousehold.members.label': 'Number of household members',
  'gileadApplication.incomeHousehold.members.placeholder': 'Enter number of household members',
  'gileadApplication.incomeHousehold.social.hint': "Enter 1111 if you don't have one",
  'gileadApplication.incomeHousehold.social.invalid': 'Please enter valid SSN',
  'gileadApplication.incomeHousehold.social.label': 'Last 4 of Social Security Number',
  'gileadApplication.incomeHousehold.social.placeholder': 'Last 4 of SSN',
  'gileadApplication.incomeHousehold.title': 'Household Income and Members',
  'gileadApplication.insuranceInformation.adap.applied.label':
    'Have you applied for the AIDS Drug Assistance Program (ADAP) or PrEP Drug Assistance Program?',
  'gileadApplication.insuranceInformation.adap.appliedDate.label': 'Application Date',
  'gileadApplication.insuranceInformation.adap.appliedDate.placeholder': 'Select application date',
  'gileadApplication.insuranceInformation.adap.denialFiles.label': 'Attach denial letter(s)',
  'gileadApplication.insuranceInformation.adap.denialFiles.placeholder': 'Upload denial letters (PDF)',
  'gileadApplication.insuranceInformation.adap.ineligibleReason.label': 'Reason',
  'gileadApplication.insuranceInformation.adap.ineligibleReason.placeholder': "Please explain why you're not eligible",
  'gileadApplication.insuranceInformation.adap.status.denied': 'Denied (include denial letter)',
  'gileadApplication.insuranceInformation.adap.status.label': 'What is your ADAP status?',
  'gileadApplication.insuranceInformation.adap.status.notApplied': 'Not applied',
  'gileadApplication.insuranceInformation.adap.status.notEligible': 'Not eligible',
  'gileadApplication.insuranceInformation.adap.status.pending': 'Pending',
  'gileadApplication.insuranceInformation.adap.status.waitListed': 'Wait-listed',
  'gileadApplication.insuranceInformation.description':
    'Please provide details about your insurance coverage and eligibility for various programs.',
  'gileadApplication.insuranceInformation.exchange.applied.label':
    'Have you applied for an insurance plan offered through a state insurance marketplace (also known as an exchange)?',
  'gileadApplication.insuranceInformation.exchange.appliedDate.label': 'Application Date',
  'gileadApplication.insuranceInformation.exchange.appliedDate.placeholder': 'Select application date',
  'gileadApplication.insuranceInformation.exchange.eligible.label':
    'Are you eligible for an insurance plan offered through a state insurance marketplace (also known as an exchange)?',
  'gileadApplication.insuranceInformation.exchange.ineligibleReason.label': 'Reason',
  'gileadApplication.insuranceInformation.exchange.ineligibleReason.placeholder': "Please explain why you're not eligible",
  'gileadApplication.insuranceInformation.medicaid.applied.label': 'Have you applied for Medicaid?',
  'gileadApplication.insuranceInformation.medicaid.appliedDate.label': 'Application Date',
  'gileadApplication.insuranceInformation.medicaid.appliedDate.placeholder': 'Select application date',
  'gileadApplication.insuranceInformation.medicaid.denialFiles.label': 'Attach denial letter(s) (if denied)',
  'gileadApplication.insuranceInformation.medicaid.denialFiles.placeholder': 'Upload denial letters (PDF)',
  'gileadApplication.insuranceInformation.medicaid.eligible.label': 'Are you eligible for Medicaid?',
  'gileadApplication.insuranceInformation.medicaid.ineligibleReason.label': 'Reason',
  'gileadApplication.insuranceInformation.medicaid.ineligibleReason.placeholder':
    'State a reason (if denied, include a copy of the denial letter)',
  'gileadApplication.insuranceInformation.medicare.applied.label': 'Have you applied for Medicare?',
  'gileadApplication.insuranceInformation.medicare.appliedDate.label': 'Application Date',
  'gileadApplication.insuranceInformation.medicare.appliedDate.placeholder': 'Select application date',
  'gileadApplication.insuranceInformation.medicare.denialFiles.label': 'Attach denial letter(s) (if denied)',
  'gileadApplication.insuranceInformation.medicare.denialFiles.placeholder': 'Upload denial letters (PDF)',
  'gileadApplication.insuranceInformation.medicare.eligible.label': 'Are you eligible for Medicare?',
  'gileadApplication.insuranceInformation.medicare.ineligibleReason.label': 'Reason',
  'gileadApplication.insuranceInformation.medicare.ineligibleReason.placeholder':
    'State a reason (if denied, include a copy of the denial letter)',
  'gileadApplication.insuranceInformation.title': 'Additional Insurance Information',
  'gileadApplication.insuranceInformation.va.benefits.label': 'Are you eligible for VA benefits?',
  'gileadApplication.insuranceInformation.va.triedMedication.label': 'Have you tried to obtain the medication through the VA?',
  'gileadApplication.navigation.decline': 'Decline',
  'gileadApplication.sign.date.label': 'Date: {date}',
  'gileadApplication.sign.description':
    'Please review and sign your Gilead Advancing Access Program application to complete the enrollment process.',
  'gileadApplication.sign.signature.label': 'Your Signature',
  'gileadApplication.sign.signature.required': 'Please sign the application',
  'gileadApplication.sign.statement':
    'By signing this application, I acknowledge that I have read and understood the terms and conditions of the Gilead Advancing Access Program. I certify that all information provided is true and accurate to the best of my knowledge.',
  'gileadApplication.sign.title': 'Sign Application',
  'gileadApplication.steps.contactAuthorization': 'Contact Authorization',
  'gileadApplication.steps.incomeHousehold': 'Income & Household',
  'gileadApplication.steps.insuranceInformation': 'Insurance Information',
  'gileadApplication.steps.sign': 'Sign',
  'gileadApplication.success.submitted': 'Application sent to provider for review',
  'gileadApplication.header.title': 'Enroll in the Gilead\u00A0Advancing\u00A0Access Program',
  'gileadApplication.header.description1': 'Your pharmacy is requesting this to help cover the cost of your prescription.',
  'gileadApplication.header.description2':
    'The program is committed to helping you afford your medication no matter your situation. Whether you have insurance or not, they can explore potential coverage options that might be right for you.',
  'gileadApplication.preview.title': 'Application',
  'gileadApplication.preview.ariaLabel': 'Gilead Advancing Access Application Preview',

  // Welcome & Onboarding
  'welcome.carousel': 'Welcome tour carousel',
  'welcome.getStarted': 'Get Started',
  'welcome.getStarted.hint': 'Begins the enrollment process',
  'welcome.screenTitle': 'Welcome Screen',
  'welcome.signIn': 'Sign In',
  'welcome.signIn.hint': 'Takes you to the login screen',
  'welcome.slides.1.description':
    'Get PrEP online! Book an online appointment with any one of our expert, 5-star Google rated telehealth providers in less than 15 minutes. Enjoy the convenience of same-day, evening, and even weekend appointments from your home, car, or office!',
  'welcome.slides.1.title': 'Fast, Online Visits',
  'welcome.slides.2.description':
    "Get PrEP online after your initial online consultation with Q Care Plus. We'll send your first PrEP (or PrEP and doxy-PEP) prescription(s) and easy-to-use testing materials to the doorstep of your choice.",
  'welcome.slides.2.title': 'Home Testing and Rx Delivery',
  'welcome.slides.3.description':
    'Get PrEP in 4 easy steps. Create your profile, meet with your provider online, complete easy testing, and get PrEP delivered to your door in discreet packaging.',
  'welcome.slides.3.title': 'How It Works',
  'welcome.slides.4.description':
    '99% of insured patients pay $0 for their medical visits, labs, and prescriptions. Getting PrEP with Q Care Plus can be free for you AND ALSO help give back to your community.',
  'welcome.slides.4.title': 'PrEP & doxy-PEP For Free?',

  // Image Upload Component
  'imageUploader.camera': 'Take photo',
  'imageUploader.cameraError.message': 'There was a problem accessing your camera. Please try again.',
  'imageUploader.cameraError.title': 'Camera Error',
  'imageUploader.gallery': 'Choose from Library',
  'imageUploader.galleryError.message': 'There was a problem accessing your photo library. Please try again.',
  'imageUploader.galleryError.title': 'Photo Library Error',
  'imageUploader.placeholder': 'Upload an image',
  'imageUploader.removePhoto': 'Remove Photo',
  'imageUploader.replaceImage.description': 'Choose how you want to replace the current image',
  'imageUploader.replaceImage.title': 'Replace Image',
  'imageUploader.tapToUpload': 'Tap to upload',
  'imageUploader.uploadedImage.a11y': 'Uploaded image',
  'imageUploader.error.read': 'Could not read the selected file. Please try again.',
  'imageUploader.requirements': 'PNG or JPG',

  // Image Crop Component
  'imageCrop.a11y.cropArea': 'Image cropping area',
  'imageCrop.a11y.cropper': 'Image cropper',
  'imageCrop.a11y.dialog': 'Crop image',
  'imageCrop.a11y.flipHorizontal': 'Flip horizontally',
  'imageCrop.a11y.flipVertical': 'Flip vertically',
  'imageCrop.a11y.rotateLeft': 'Rotate left',
  'imageCrop.a11y.rotateRight': 'Rotate right',
  'imageCrop.error': 'There was an issue. Please try again.',
  'imageCrop.idGuide': 'Drag to reposition, pinch or scroll to zoom, and adjust the frame to fit your entire ID card',
  'imageCrop.control.rotateLeft': 'Rotate left',
  'imageCrop.control.rotateRight': 'Rotate right',
  'imageCrop.control.flipH': 'Flip horizontal',
  'imageCrop.control.flipV': 'Flip vertical',

  // File Uploader Component
  'fileUploader.a11y.dropzone': '{title}. Press to upload.',
  'fileUploader.a11y.dropzoneDefault': 'File uploader. Press to upload.',
  'fileUploader.a11y.errors': 'File upload errors',
  'fileUploader.a11y.errorsForFile': 'Errors for file {path}',
  'fileUploader.a11y.preview': 'Preview of {name}',
  'fileUploader.cameraHelp.library':
    "Still not working? Take the photo with your phone's camera app, then come back here and upload it from your photo library.",
  'fileUploader.cameraHelp.settings':
    "If the camera opens but the screen stays black, open your phone's Settings, find the browser you are using, and turn on Camera.",
  'fileUploader.cameraHelp.title': 'Camera not working?',
  'fileUploader.clickToUpload': 'Click to upload',
  'fileUploader.defaultAllowed': 'SVG, PNG, JPG or GIF',
  'fileUploader.dragAndDrop': 'or drag and drop',
  'fileUploader.uploadNewImage': 'Upload new image',

  // Document Uploader Component
  'documentUploader.a11y.confirmUploadAll': 'Confirm and upload all files',
  'documentUploader.a11y.dropzone': 'File upload dropzone',
  'documentUploader.a11y.fileName': 'File name: {name}',
  'documentUploader.a11y.fileStatus': 'File status: {status}',
  'documentUploader.a11y.input': 'File upload input',
  'documentUploader.a11y.pdfPreview': 'PDF preview',
  'documentUploader.a11y.previewFile': 'Preview {name}',
  'documentUploader.a11y.removeFile': 'Remove {name}',
  'documentUploader.a11y.uploading': 'Uploading file',
  'documentUploader.error.unknown': 'Unknown error, please try again',
  'documentUploader.status.completed': 'Completed',
  'documentUploader.status.failed': 'Failed',
  'documentUploader.status.loading': 'Loading',
  'documentUploader.status.pressToPreview': 'Press to preview',
  'documentUploader.uploadAll': 'Upload All',

  // PDF Viewer Component
  'pdfViewer.a11y.viewer': 'PDF Viewer',
  'pdfViewer.a11y.document': 'PDF Document',
  'pdfViewer.a11y.content': 'PDF Content',
  'pdfViewer.a11y.controls': 'PDF Controls',
  'pdfViewer.a11y.page': 'Page {current} of {total}',
  'pdfViewer.a11y.expand': 'Expand PDF to full screen',
  'pdfViewer.a11y.close': 'Close PDF full screen view',
  'pdfViewer.a11y.zoomIn': 'Zoom In',
  'pdfViewer.a11y.zoomOut': 'Zoom Out',
  'pdfViewer.a11y.rotateLeft': 'Rotate Left',
  'pdfViewer.a11y.rotateRight': 'Rotate Right',
  'pdfViewer.a11y.download': 'Download PDF',
  'pdfViewer.a11y.previousPage': 'Previous Page',
  'pdfViewer.a11y.nextPage': 'Next Page',
  'pdfViewer.error.download': 'Failed to download PDF. Please try again.',

  // Carousel Component
  'carousel.accessibility.currentSlide': 'Current slide',
  'carousel.accessibility.goToSlide': 'Go to this slide',
  'carousel.accessibility.hint': 'Swipe left or right to navigate between slides',
  'carousel.accessibility.nextSlide': 'Next slide',
  'carousel.accessibility.paginationDot': 'Slide {current} of {total}',
  'carousel.accessibility.previousSlide': 'Previous slide',
  'carousel.accessibility.slideAnnouncement': 'Slide {current} of {total}. {title}. {description}',
  'carousel.default.label': 'Image carousel',

  // Mini Game (2048)
  'miniGame.score': 'Score: {score}',
  'miniGame.faq': 'FAQ',
  'miniGame.faq.close': 'Got it!',
  'miniGame.win.title': 'You Win!',
  'miniGame.gameOver.title': 'Game Over!',
  'miniGame.button.continue': 'Continue',
  'miniGame.button.newGame': 'New Game',
  'miniGame.faq.goal.title': 'Goal of the Game',
  'miniGame.faq.goal.description':
    'Slide numbered tiles on a grid to combine them and create a 2048 tile. Continue playing after reaching 2048 to achieve higher scores!',
  'miniGame.faq.howToPlay.title': 'How to Play',
  'miniGame.faq.howToPlay.desktop': 'On Laptop/Desktop:',
  'miniGame.faq.howToPlay.desktopDescription': 'Use arrow keys to move tiles up, down, left, or right.',
  'miniGame.faq.howToPlay.mobile': 'On Phone/Tablet:',
  'miniGame.faq.howToPlay.mobileDescription': 'Swipe in the direction you want the tiles to move.',
  'miniGame.faq.tips.title': 'Tips to Play',
  'miniGame.faq.tips.planAhead': 'Plan Ahead:',
  'miniGame.faq.tips.planAheadDescription': 'Think a few moves ahead to maximize combinations.',
  'miniGame.faq.tips.corner': 'Keep Your Highest Tile in a Corner:',
  'miniGame.faq.tips.cornerDescription': 'Helps manage larger tiles and keeps them organized.',
  'miniGame.faq.tips.monotonic': 'Build Monotonic Rows or Columns:',
  'miniGame.faq.tips.monotonicDescription': 'Arrange tiles so their values increase or decrease consistently.',
  'miniGame.faq.tips.dontRush': "Don't Rush:",
  'miniGame.faq.tips.dontRushDescription': 'Make strategic moves instead of moving tiles randomly.',

  // *** 10. FINANCIAL ***

  // Payment
  'payment.button.pay': 'Pay {amount}',
  'payment.button.processing': 'Processing...',
  'payment.completed.button': 'Return to Home',
  'payment.completed.message': 'Your payment has been successfully processed',
  'payment.completed.title': 'Payment Successful',
  'payment.error.general': 'Something went wrong. Please try again.',
  'payment.error.processing': 'Payment could not be processed. Please try again.',
  'payment.error.setup': 'Unable to set up payment. Please check your connection.',
  'payment.error.timeout': 'Payment setup is taking longer than expected.',
  'payment.form.title': 'Complete Your Payment',
  'payment.form.description': 'Add your payment method to process the payment securely',
  'payment.header.subtitle': "Your previous payment didn't go through. Let's get that sorted out quickly and securely.",
  'payment.header.title': 'Complete Your Payment',
  'payment.success.message': 'Payment successful!',
  'payment.title': 'Payment',
  'webview.error.untrusted.title': 'This link cannot be opened',
  'webview.error.untrusted.message':
    'This link points outside QCare Plus, so it was blocked to keep your account safe. Please go back and try again.',

  'medReminders.title': 'Med Reminders',
  'medReminders.description': 'Reminders stay on this phone and notify you at the times you choose.',
  'medReminders.add': 'Add a medication',
  'medReminders.everyDay': 'Every day',
  'medReminders.empty.title': 'No medications yet',
  'medReminders.empty.description': "Add one and we'll remind you at the times and on the days you choose.",
  'medReminders.notificationsOff.title': 'Notifications are off',
  'medReminders.notificationsOff.description': 'Turn them on so your reminders can reach you',
  'medReminders.notification.channel': 'Medication reminders',
  'medReminders.notification.title': 'Time to take your medication',
  'medReminders.notification.body': '{name}, {dose}',
  'medReminders.discreet.title': 'Discreet notifications',
  'medReminders.discreet.description': 'Leave the medication out of the reminder',
  'medReminders.card.toggle.a11y.label': 'Reminders for {name}',
  'medReminders.card.on': 'Reminders on',
  'medReminders.card.off': 'Paused',
  'medReminders.form.title.new': 'Add Medication',
  'medReminders.form.title.edit': 'Edit Medication',
  'medReminders.form.description': "You'll get a notification at each time you set.",
  'medReminders.form.name.label': 'Name',
  'medReminders.form.name.placeholder': 'e.g. Truvada',
  'medReminders.form.dose.label': 'Dose (optional)',
  'medReminders.form.dose.placeholder': 'e.g. 1 Tablet',
  'medReminders.form.times.label': 'Reminder Times',
  'medReminders.form.times.add': 'Add another time',
  'medReminders.form.times.picker.title': 'Pick a time',
  'medReminders.form.times.remove': 'Remove time',
  'medReminders.form.times.change': 'Change time',
  'medReminders.form.days.label': 'Days of the week',
  'medReminders.form.save': 'Save',
  'medReminders.form.delete': 'Delete',
  'medReminders.form.delete.title': 'Delete this medication?',
  'medReminders.form.delete.a11y.hint': 'Deletes this medication and its reminders',
  'medReminders.form.delete.message': 'Your reminders for {name} will stop.',
  'medReminders.form.validation.name': 'Enter the medication name',
  'medReminders.weekday.sun.short': 'Sun',
  'medReminders.weekday.mon.short': 'Mon',
  'medReminders.weekday.tue.short': 'Tue',
  'medReminders.weekday.wed.short': 'Wed',
  'medReminders.weekday.thu.short': 'Thu',
  'medReminders.weekday.fri.short': 'Fri',
  'medReminders.weekday.sat.short': 'Sat',
  'medReminders.weekday.sun.long': 'Sunday',
  'medReminders.weekday.mon.long': 'Monday',
  'medReminders.weekday.tue.long': 'Tuesday',
  'medReminders.weekday.wed.long': 'Wednesday',
  'medReminders.weekday.thu.long': 'Thursday',
  'medReminders.weekday.fri.long': 'Friday',
  'medReminders.weekday.sat.long': 'Saturday',
}

export default en
